import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import '@/App.css';
import MobileLandingPage from '@/MobileLandingPage';
import { BrowserRouter, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Clock, CheckCircle, XCircle, Shuffle, Archive, Plus, Trash2, Copy, Users, TrendingUp, Award, Upload, Download, Lightbulb, Wand2, ChevronDown, ChevronUp, Mic, Keyboard, StopCircle, Maximize2 } from 'lucide-react';
import { toast } from 'sonner';
import Logo from './components/Logo';
import Header from './components/Header';
import Footer from './components/Footer';
import InsightSummaryCard from './components/InsightSummary';
import ReputationBadge from './components/ReputationBadge';
import ReframingTool from './components/ReframingTool';
import SplitTimer from './components/SplitTimer';
import FeatureDetailPage from './components/FeatureDetailPage';
import ContentManagementTab from './components/ContentManagementTab';
import AnalyticsTab from './components/AnalyticsTab';
import AboutPage from './components/AboutPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Imprint from './components/Imprint';
import ArchivePage from './components/ArchivePage';
import ArchivedDebateView from './components/ArchivedDebateView';
import FeedbackModal from './components/FeedbackModal';
import FeedbackAdmin from './components/FeedbackAdmin';
import AdminLogin from './components/AdminLogin';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import DesignManager from './components/DesignManager';
import GenericFunFactsManager from './components/GenericFunFactsManager';
import ChooseMode from './components/ChooseMode';
import UserLogin from './components/UserLogin';
import UserRegister from './components/UserRegister';
import Terms from './components/Terms';
import LegalEditor from './components/LegalEditor';
import AboutEditor from './components/AboutEditor';
import PricingPage from './components/PricingPage';
import VoucherManager from './components/VoucherManager';
import DebateRoomNew from './components/DebateRoomNew';
import DebateRoomRouter from './components/DebateRoomRouter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ========================================
// DESIGN CONTEXT - Manages custom design images
// ========================================
const DesignContext = createContext(null);

export const useDesign = () => {
  const context = useContext(DesignContext);
  if (!context) {
    throw new Error('useDesign must be used within DesignProvider');
  }
  return context;
};

const DesignProvider = ({ children }) => {
  const [designConfig, setDesignConfig] = useState({
    landing_desktop: '/LANDINGPAGE_Back4k.jpg',
    landing_mobile: '/LANDINGPAGE_Mobile3000.jpg',
    debate_bg_desktop: '/LANDINGPAGEBACKCLEAN.jpg',
    debate_bg_mobile: '/UI_Back_vertical_clean.jpg',
    debate_header_desktop: '/Header_Full_V03.jpg',
    debate_header_mobile: '/UI_verticalHeader04.jpg'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDesignConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/design`);
        setDesignConfig(response.data);
        console.log('✅ Custom design configuration loaded:', response.data);
      } catch (error) {
        console.warn('⚠️ Using default design configuration:', error);
        // Keep default values if API fails
      } finally {
        setLoading(false);
      }
    };

    fetchDesignConfig();
  }, []);

  return (
    <DesignContext.Provider value={{ designConfig, loading }}>
      {children}
    </DesignContext.Provider>
  );
};

// Landing Page Wrapper - decides between Mobile and Desktop
const LandingPageWrapper = () => {
  const [isMobile] = useState(window.innerWidth <= 1080);
  
  if (isMobile) {
    return <MobileLandingPage />;
  }
  
  return <LandingPage />;
};

// Desktop Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const { designConfig } = useDesign();
  
  const handleFeatureClick = async (feature, path) => {
    // Track click
    try {
      await axios.post(`${API}/analytics/track`, {
        event_type: 'click',
        element: feature,
        page: 'landing'
      });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
    navigate(path);
  };

  const handleArchiveClick = () => {
    // Check if user is logged in
    const sessionToken = localStorage.getItem('user_session_token');
    
    if (sessionToken) {
      // User is logged in - navigate to archive
      navigate('/archive');
    } else {
      // User is not logged in - redirect to login with return path
      navigate('/login?redirect=/archive');
    }
  };

  return (
    <div 
      className="landing-container-new"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}${designConfig.landing_desktop})`
      }}
    >
      <div className="landing-content-new">
        {/* Logo Section - Combined Logo with Icon, Text and Claim */}
        <div className="landing-logo-section">
          <img 
            src="/debatify-typo-landingpage4.svg" 
            alt="Debatefy - Where minds meet to grow" 
            className="landing-combined-logo"
          />
        </div>
        
        {/* Start Debate Button - Navigates to choose mode */}
        <Button 
          data-testid="start-debate-btn"
          className="landing-cta-button" 
          size="lg"
          onClick={() => navigate('/choose-mode')}
        >
          START DEBATE
        </Button>
        
        {/* Feature Cards - Clickable */}
        <div className="landing-features-grid">
          <div 
            className="landing-feature-card"
            onClick={() => handleFeatureClick('factchecking', '/factchecking')}
            role="button"
            tabIndex={0}
          >
            <div className="landing-feature-icon-wrapper">
              <CheckCircle className="landing-feature-icon" />
            </div>
            <h3>Fact-Checking</h3>
            <p>Real-time verification through Perplexity AI ensures every argument is grounded in truth</p>
          </div>
          
          <div 
            className="landing-feature-card"
            onClick={() => handleFeatureClick('insight-scoring', '/insight-scoring')}
            role="button"
            tabIndex={0}
          >
            <div className="landing-feature-icon-wrapper">
              <Lightbulb className="landing-feature-icon" />
            </div>
            <h3>Insight Scoring</h3>
            <p>Rewards depth of thought and new perspectives, not rhetorical victories</p>
          </div>
          
          <div 
            className="landing-feature-card"
            onClick={() => handleFeatureClick('collaborative-growth', '/collaborative-growth')}
            role="button"
            tabIndex={0}
          >
            <div className="landing-feature-icon-wrapper">
              <Users className="landing-feature-icon" />
            </div>
            <h3>Collaborative Growth</h3>
            <p>Split-timer system gives space for thinking without time pressure</p>
          </div>
        </div>
        
        {/* Secondary Actions */}
        <div className="landing-secondary-actions">
          <button 
            className="landing-secondary-btn"
            onClick={handleArchiveClick}
            data-testid="view-archive-btn"
          >
            archive
          </button>
          <button 
            className="landing-secondary-btn"
            onClick={() => navigate('/pricing')}
            data-testid="pricing-btn"
          >
            pricing
          </button>
          <button 
            className="landing-secondary-btn"
            onClick={() => navigate('/admin')}
            data-testid="admin-panel-btn"
          >
            admin
          </button>
          <button 
            className="landing-secondary-btn"
            onClick={() => navigate('/about')}
            data-testid="about-btn"
          >
            about
          </button>
        </div>
      </div>
    </div>
  );
};

// Session Setup
const SessionSetup = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

const handleCreateSession = async () => {
    if (!name) {
      toast.error('Please enter your name');
      return;
    }

    setLoading(true);
    try {
      // Creator is always Protagonist A
      const response = await axios.post(`${API}/session/create`, {
        participant_name: name,
        role: 'protagonist_a'
      });
      
      localStorage.setItem('session_id', response.data.id);
      localStorage.setItem('participant_name', name);
      localStorage.setItem('participant_role', 'protagonist_a');
      
      toast.success('Session created!');
      navigate('/configure');
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Error creating session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <Header />
      <Card className="config-card">
        <CardHeader>
          <CardTitle>Session Setup</CardTitle>
          <CardDescription>Choose your name</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Your Name (max 24 characters)</Label>
            <Input
              id="name"
              data-testid="participant-name-input"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 24))}
              maxLength={24}
            />
          </div>
          
               <p className="text-sm text-muted-foreground text-center">
              As debate creator, you will be <strong>Protagonist A</strong> and start the debate
            </p>
         
          
          <Button 
            data-testid="continue-setup-btn"
            className="w-full" 
            onClick={handleCreateSession}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Continue to Configuration'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Debate Configuration
const DebateConfig = () => {
  const navigate = useNavigate();
  const [languages, setLanguages] = useState({});
  const [theses, setTheses] = useState([]);
  const [config, setConfig] = useState({
    language: 'en',
    total_time_limit: 30,
    argument_time_limit: 600,
    thesis: '',
    custom_thesis: '',
    llm_model: null,
    is_ai_opponent: false  // NEW: AI opponent toggle
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLanguages();
  }, []);

  useEffect(() => {
    fetchTheses(config.language);
  }, [config.language]);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/languages`);
      setLanguages(response.data);
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  };

  const fetchTheses = async (language) => {
    try {
      const response = await axios.get(`${API}/theses?language=${language}`);
      setTheses(response.data);
      // Reset thesis selection when language changes
      setConfig(prev => ({ ...prev, thesis: '' }));
    } catch (error) {
      console.error('Error fetching theses:', error);
    }
  };

  const selectRandomModel = async () => {
    try {
      const response = await axios.get(`${API}/models/random`);
      setConfig({ ...config, llm_model: response.data });
      toast.success(`Model selected: ${response.data.name}`);
    } catch (error) {
      console.error('Error selecting random model:', error);
      toast.error('Error selecting model');
    }
  };

  const startDebate = async () => {
    const sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      toast.error('No session found. Please setup again.');
      navigate('/setup');
      return;
    }

    const finalThesis = config.thesis || config.custom_thesis;
    if (!finalThesis) {
      toast.error('Please select or enter a thesis');
      return;
    }

    if (!config.llm_model) {
      toast.error('Please select an LLM model');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/debate/create`, {
        language: config.language,
        total_time_limit: parseInt(config.total_time_limit),
        argument_time_limit: parseInt(config.argument_time_limit),
        thesis: finalThesis,
        llm_model: config.llm_model,
        session_a_id: sessionId,
        session_b_id: sessionId,
        is_ai_opponent: config.is_ai_opponent  // NEW: Send AI flag
      });
      
      localStorage.setItem('debate_id', response.data.id);
      
      // If AI opponent, skip invite page and go directly to debate
      if (config.is_ai_opponent) {
        toast.success('Debate vs AI started! 🤖');
        navigate(`/debate/${response.data.id}`);
      } else {
        toast.success('Debate created!');
        navigate(`/invite/${response.data.invite_code}`);
      }
    } catch (error) {
      console.error('Error creating debate:', error);
      toast.error('Error starting debate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <Header />
      <Card className="config-card">
        <CardHeader>
          <CardTitle>Debate Configuration</CardTitle>
          <CardDescription>Configure debate parameters</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Language</Label>
            <Select value={config.language} onValueChange={(val) => setConfig({ ...config, language: val })}>
              <SelectTrigger data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(languages).map(([code, name]) => (
                  <SelectItem key={code} value={code}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="total-time">Total Time (minutes)</Label>
              <Input
                id="total-time"
                data-testid="total-time-input"
                type="number"
                value={config.total_time_limit}
                onChange={(e) => setConfig({ ...config, total_time_limit: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="arg-time">Time per Argument (seconds)</Label>
              <Input
                id="arg-time"
                data-testid="argument-time-input"
                type="number"
                value={config.argument_time_limit}
                onChange={(e) => setConfig({ ...config, argument_time_limit: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Select Thesis</Label>
            <Select value={config.thesis} onValueChange={(val) => setConfig({ ...config, thesis: val })}>
              <SelectTrigger data-testid="thesis-select">
                <SelectValue placeholder="Select thesis..." />
              </SelectTrigger>
              <SelectContent>
                {theses.map((thesis) => (
                  <SelectItem key={thesis.id} value={thesis.text}>
                    {thesis.text.substring(0, 60)}...
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom-thesis">Or Enter Custom Thesis</Label>
            <Textarea
              id="custom-thesis"
              data-testid="custom-thesis-input"
              placeholder="Enter your custom thesis..."
              value={config.custom_thesis}
              onChange={(e) => setConfig({ ...config, custom_thesis: e.target.value })}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>AI Model</Label>
            <div className="flex gap-2">
              <Button
                data-testid="random-model-btn"
                variant="outline"
                className="flex-1"
                onClick={selectRandomModel}
              >
                <Shuffle className="mr-2 h-4 w-4" />
                Random Model
              </Button>
              {config.llm_model && (
                <Badge className="self-center" variant="secondary">
                  {config.llm_model.name}
                </Badge>
              )}
            </div>
          </div>

          <div className="space-y-4 border-t pt-4">
            <Label className="text-base font-semibold">Choose Your Opponent</Label>
            <div className="space-y-2">
              <div 
                className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition ${!config.is_ai_opponent ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`}
                onClick={() => setConfig({ ...config, is_ai_opponent: false })}
              >
                <input 
                  type="radio" 
                  name="opponent" 
                  checked={!config.is_ai_opponent}
                  onChange={() => setConfig({ ...config, is_ai_opponent: false })}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-medium">👥 Human Opponent</div>
                  <div className="text-sm text-gray-500">Invite a friend via link</div>
                </div>
              </div>
              
              <div 
                className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition ${config.is_ai_opponent ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`}
                onClick={() => setConfig({ ...config, is_ai_opponent: true })}
              >
                <input 
                  type="radio" 
                  name="opponent" 
                  checked={config.is_ai_opponent}
                  onChange={() => setConfig({ ...config, is_ai_opponent: true })}
                  className="w-4 h-4"
                />
                <div>
                  <div className="font-medium">🤖 AI Opponent</div>
                  <div className="text-sm text-gray-500">Start immediately - practice against AI</div>
                </div>
              </div>
            </div>
          </div>

          <Button 
            data-testid="start-debate-config-btn"
            className="w-full" 
            onClick={startDebate}
            disabled={loading}
          >
            {loading ? 'Starting...' : 'Start Debate'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Invite Page
const InvitePage = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [debate, setDebate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDebate();
    const interval = setInterval(fetchDebate, 2000);
    return () => clearInterval(interval);
  }, [inviteCode]);

  const fetchDebate = async () => {
    try {
      const response = await axios.get(`${API}/debate/by-invite/${inviteCode}`);
      setDebate(response.data);
      setLoading(false);
      
      if (response.data.session_b_id && response.data.status === 'active') {
        navigate(`/debate/${response.data.id}`);
      }
    } catch (error) {
      console.error('Error fetching debate:', error);
      setLoading(false);
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/join/${inviteCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied!');
  };

  if (loading) return <div className="page-container">Loading...</div>;
  if (!debate) return <div className="page-container">Debate not found</div>;

  return (
    <div className="page-container">
      <Header />
      <Card className="config-card">
        <CardHeader>
          <CardTitle>Waiting for Protagonist B</CardTitle>
          <CardDescription>Share the invite link with your debate partner</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="invite-info">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-8 w-8 text-purple-600" />
              <div>
                <h3 className="font-semibold">Debate Ready</h3>
                <p className="text-sm text-muted-foreground">Thesis: {debate.thesis}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Invitation Code</Label>
            <div className="flex gap-2">
              <Input 
                value={inviteCode} 
                readOnly 
                className="font-mono text-lg"
                data-testid="invite-code-display"
              />
              <Button 
                variant="outline" 
                onClick={copyInviteLink}
                data-testid="copy-invite-link-btn"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-sm">
              <strong>Invite Link:</strong>
              <br />
              <code className="text-xs break-all">
                {window.location.origin}/join/{inviteCode}
              </code>
            </p>
          </div>

          <div className="text-center py-8">
            <div className="animate-pulse mb-4">
              <Clock className="h-12 w-12 mx-auto text-purple-600" />
            </div>
            <p className="text-muted-foreground">
              Waiting for Protagonist B...
            </p>
          </div>

          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate(`/debate/${debate.id}`)}
            data-testid="start-anyway-btn"
          >
            Start Anyway
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Join Page
const JoinPage = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [debate, setDebate] = useState(null);

  useEffect(() => {
    fetchDebate();
  }, [inviteCode]);

  const fetchDebate = async () => {
    try {
      const response = await axios.get(`${API}/debate/by-invite/${inviteCode}`);
      setDebate(response.data);
    } catch (error) {
      console.error('Error fetching debate:', error);
      toast.error('Invalid invitation code');
    }
  };

  const handleJoin = async () => {
    if (!name) {
      toast.error('Please enter your name');
      return;
    }

    setLoading(true);
    try {
      const sessionResponse = await axios.post(`${API}/session/create`, {
        participant_name: name,
        role: 'protagonist_b'
      });
      
      localStorage.setItem('session_id', sessionResponse.data.id);
      localStorage.setItem('participant_name', name);
      localStorage.setItem('participant_role', 'protagonist_b');
      
      const joinResponse = await axios.post(`${API}/debate/join/${inviteCode}?session_id=${sessionResponse.data.id}`);
      
      localStorage.setItem('debate_id', joinResponse.data.id);
      toast.success('Successfully joined the debate!');
      navigate(`/debate/${joinResponse.data.id}`);
    } catch (error) {
      console.error('Error joining debate:', error);
      toast.error('Error joining debate');
    } finally {
      setLoading(false);
    }
  };

  if (!debate) return <div className="page-container">Loading...</div>;

  return (
    <div className="page-container">
      <Header />
      <Card className="config-card">
        <CardHeader>
          <CardTitle>Join Debate</CardTitle>
          <CardDescription>You have been invited to a debate</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-semibold mb-2">Debate Thesis:</h3>
            <p className="text-sm">{debate.thesis}</p>
            <div className="mt-2 flex gap-2">
              <Badge variant="outline">{debate.llm_model.name}</Badge>
              <Badge variant="outline">{debate.total_time_limit} Min</Badge>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="join-name">Your Name (max 24 characters)</Label>
            <Input
              id="join-name"
              data-testid="join-name-input"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 24))}
              maxLength={24}
            />
            <p className="text-xs text-muted-foreground">
              You will participate as Protagonist B
            </p>
          </div>

          <Button 
            data-testid="join-debate-btn"
            className="w-full" 
            onClick={handleJoin}
            disabled={loading}
          >
            {loading ? 'Joining...' : 'Join Debate'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Debate Room
const DebateRoom = () => {
  const navigate = useNavigate();
  const { designConfig } = useDesign();
  const debateId = window.location.pathname.split('/').pop();
  const [debate, setDebate] = useState(null);
  const [debateArguments, setDebateArguments] = useState([]);
  const [newArgument, setNewArgument] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [showReframing, setShowReframing] = useState({});
  const [expandedDetails, setExpandedDetails] = useState({});  // Track which argument details are expanded
  const [insightExpanded, setInsightExpanded] = useState(false);  // Track insight summary expansion
  const [hasAutoStarted, setHasAutoStarted] = useState(false);  // Track if timer has been auto-started
  
  // Total Debate Time (runs continuously once started)
  const [totalTimeRemaining, setTotalTimeRemaining] = useState(0);
  const [totalTimeStarted, setTotalTimeStarted] = useState(false);
  
  // Per-Argument Time (Chess Clock - pauses after each argument)
  const [timeRemainingA, setTimeRemainingA] = useState(0);
  const [timeRemainingB, setTimeRemainingB] = useState(0);
  const [currentTurn, setCurrentTurn] = useState(null); // 'a' or 'b'
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [thinkingTime, setThinkingTime] = useState(0);
  const [typingTime, setTypingTime] = useState(0);
  const splitTimerRef = useRef(null);
  const timersInitializedRef = useRef(false); // Track if timers have been initialized
  
  // New UI states
  const [inputMode, setInputMode] = useState('keyboard'); // 'keyboard' or 'microphone'
  const [isRecording, setIsRecording] = useState(false);
  const [expandedArgument, setExpandedArgument] = useState(null); // For modal
  const [activeContextButton, setActiveContextButton] = useState({}); // Track which context buttons are active
  const [opponentName, setOpponentName] = useState('Opponent'); // Store opponent's name
  const [opponentIcon, setOpponentIcon] = useState(null); // Store AI opponent icon
  const [showInsightsModal, setShowInsightsModal] = useState(false); // Insights modal
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false); // Feedback modal
  const [feedbackArgument, setFeedbackArgument] = useState(null); // Argument for feedback
  
  // TTS States
  const [playingArgumentId, setPlayingArgumentId] = useState(null); // Which argument is currently playing
  const [ttsSpeed, setTtsSpeed] = useState(0.9); // Speech rate (0.5 - 2.0)
  
  // Mobile States - User-Agent + Viewport Width Detection
  const [isMobile, setIsMobile] = useState(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    const isNarrowViewport = window.innerWidth <= 1080;
    return isMobileUA || isNarrowViewport;
  });
  const [showMobileContext, setShowMobileContext] = useState(false);
  const [mobileContextData, setMobileContextData] = useState(null);
  const mobileTextareaRef = useRef(null);
  
  // AI Status Bar States
  const [aiStatus, setAiStatus] = useState({ show: false, phase: 'thinking', remaining: 0, total: 0 });
  
  // Fun Facts Box States
  const [showFunFacts, setShowFunFacts] = useState(false);
  const [funFacts, setFunFacts] = useState([]);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [funFactsCollapsed, setFunFactsCollapsed] = useState(false); // Startet immer ausgeklappt
  const [loadingFacts, setLoadingFacts] = useState(false);
  const [thesisSpecificFacts, setThesisSpecificFacts] = useState([]); // Thesis-specific facts
  const [genericFactsPool, setGenericFactsPool] = useState([]); // Pool of generic facts
  const [isFirstArgument, setIsFirstArgument] = useState(true); // Track if it's the first argument
  const funFactsTimerRef = useRef(null);
  const hasLoadedPreference = useRef(false); // Track if we've loaded localStorage preference
  
  // Update mobile state on resize
  useEffect(() => {
    const handleResize = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isNarrowViewport = window.innerWidth <= 1080;
      setIsMobile(isMobileUA || isNarrowViewport);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle keyboard with visualViewport for mobile
  useEffect(() => {
    if (!isMobile || !window.visualViewport) return;

    const handleViewportChange = () => {
      const inputArea = document.querySelector('.mobile-input-area');
      if (!inputArea) return;

      const viewport = window.visualViewport;
      const offsetY = viewport.height - window.innerHeight;
      
      // Wenn Keyboard sichtbar ist (offsetY < 0), positioniere Input-Area darüber
      if (offsetY < -50) {
        // Keyboard ist offen
        inputArea.style.transform = `translateY(${offsetY}px)`;
      } else {
        // Keyboard ist zu
        inputArea.style.transform = 'translateY(0)';
      }
    };

    window.visualViewport.addEventListener('resize', handleViewportChange);
    window.visualViewport.addEventListener('scroll', handleViewportChange);

    return () => {
      window.visualViewport.removeEventListener('resize', handleViewportChange);
      window.visualViewport.removeEventListener('scroll', handleViewportChange);
    };
  }, [isMobile]);

  useEffect(() => {
    // Reset initialization flag when debate ID changes
    timersInitializedRef.current = false;
    fetchDebate();
    fetchArguments();
    const interval = setInterval(() => {
      fetchDebate();
      fetchArguments();
    }, 5000);
    return () => clearInterval(interval);
  }, [debateId]);
  
  useEffect(() => {
    // Only initialize timers ONCE when debate is first loaded
    if (debate && debate.argument_time_limit && !timersInitializedRef.current) {
      // Calculate total time remaining based on total_time_started_at
      if (debate.total_time_started_at) {
        const startedAt = new Date(debate.total_time_started_at);
        const now = new Date();
        const elapsedSeconds = Math.floor((now - startedAt) / 1000);
        const totalSeconds = (debate.total_time_limit * 60) - elapsedSeconds;
        setTotalTimeRemaining(Math.max(0, totalSeconds));
        setTotalTimeStarted(true); // Timer is already running
      } else {
        // Timer not started yet
        const totalSeconds = debate.total_time_limit * 60 || 0;
        setTotalTimeRemaining(totalSeconds);
        setTotalTimeStarted(false);
      }
      
      // Per-argument time from argument_time_limit (already in seconds from backend)
      const perArgumentTime = debate.argument_time_limit || 0;
      setTimeRemainingA(perArgumentTime);
      setTimeRemainingB(perArgumentTime);
      
      timersInitializedRef.current = true; // Mark as initialized
      console.log('Timers initialized ONCE');
    }
    
    // Continuous synchronization: Update total timer if it has started
    if (debate && debate.total_time_started_at && timersInitializedRef.current) {
      const startedAt = new Date(debate.total_time_started_at);
      const now = new Date();
      const elapsedSeconds = Math.floor((now - startedAt) / 1000);
      const totalSeconds = (debate.total_time_limit * 60) - elapsedSeconds;
      const syncedTime = Math.max(0, totalSeconds);
      
      // Only update if difference is significant (> 2 seconds) to avoid constant updates
      if (Math.abs(totalTimeRemaining - syncedTime) > 2) {
        setTotalTimeRemaining(syncedTime);
        console.log('Total timer re-synced:', syncedTime);
      }
      
      // Ensure timer is running if backend shows it started
      if (!totalTimeStarted) {
        setTotalTimeStarted(true);
      }
    }
  }, [debate]);

  // Total Time Countdown (runs continuously once started)
  useEffect(() => {
    if (!totalTimeStarted) return;

    const totalTimer = setInterval(() => {
      setTotalTimeRemaining(prev => {
        const newTime = Math.max(0, prev - 1);
        if (newTime === 0) {
          // Total time expired - debate ends, highest score wins
          handleTotalTimeExpired();
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(totalTimer);
  }, [totalTimeStarted]);

  // Per-Argument Timer countdown (Chess Clock - only runs when typing)
  useEffect(() => {
    console.log('[Timer] Check conditions:', { isTimerRunning, currentTurn, debateId: debate?.id });
    
    if (!isTimerRunning || !currentTurn || !debate?.id) {
      console.log('[Timer] Not running - conditions not met');
      return;
    }

    console.log('[Timer] ✓ Starting interval for turn:', currentTurn);
    const timer = setInterval(() => {
      if (currentTurn === 'a') {
        setTimeRemainingA(prev => {
          const newTime = Math.max(0, prev - 1);
          if (newTime === 0) {
            setIsTimerRunning(false);
            toast.warning('⏰ Time is up for this argument, Protagonist A!');
          }
          return newTime;
        });
      } else if (currentTurn === 'b') {
        setTimeRemainingB(prev => {
          const newTime = Math.max(0, prev - 1);
          if (newTime === 0) {
            setIsTimerRunning(false);
            toast.warning('⏰ Time is up for this argument, Protagonist B!');
          }
          return newTime;
        });
      }
    }, 1000);

    return () => {
      console.log('[Timer] ⚠️ Cleanup - clearing interval');
      clearInterval(timer);
    };
  }, [isTimerRunning, currentTurn, debate?.id]); // ← NUR debate.id, nicht das ganze Objekt!

  // Mobile detection via User-Agent (no resize listener needed)
  useEffect(() => {
    if (isMobile) {
      document.documentElement.classList.add('mobile-active');
    } else {
      document.documentElement.classList.remove('mobile-active');
    }
    
    return () => {
      document.documentElement.classList.remove('mobile-active');
    };
  }, [isMobile]);

  // Auto-resize mobile textarea
  useEffect(() => {
    if (isMobile && mobileTextareaRef.current) {
      const textarea = mobileTextareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  }, [newArgument, isMobile]);

  // Handle total time expiration - Highest score wins
  const handleTotalTimeExpired = async () => {
    try {
      toast.error('⏰ Total debate time has expired!');
      
      // Determine winner by score
      let winner = null;
      if (debate.score_a > debate.score_b) {
        winner = 'a';
        toast.success('🏆 Protagonist A wins by higher score!');
      } else if (debate.score_b > debate.score_a) {
        winner = 'b';
        toast.success('🏆 Protagonist B wins by higher score!');
      } else {
        toast.info('🤝 Debate ends in a draw!');
      }
      
      // Complete debate
      await axios.patch(`${API}/debate/${debateId}/complete`, {
        winner: winner,
        completion_reason: 'total_time_expired'
      });
      
      // Wait a moment then redirect
      setTimeout(() => {
        navigate(`/archive/view/${debateId}`);
      }, 3000);
    } catch (error) {
      console.error('Error handling total time expiration:', error);
      toast.error('Failed to end debate. Please complete manually.');
    }
  };

  const fetchDebate = async () => {
    try {
      const response = await axios.get(`${API}/debate/${debateId}`);
      const debateData = response.data;
      setDebate(debateData);
      // Timer initialization is handled by useEffect, not here
      
      // Update current turn from backend
      if (debateData.current_turn) {
        setCurrentTurn(debateData.current_turn);
        console.log('[Fetch Debate] Current turn from backend:', debateData.current_turn);
      }
      
      // Fetch opponent's name
      const myRole = localStorage.getItem('participant_role');
      const opponentSessionId = myRole === 'protagonist_a' ? debateData.session_b_id : debateData.session_a_id;
      if (opponentSessionId) {
        // Check if AI opponent
        if (debateData.is_ai_opponent && opponentSessionId.startsWith('ai_opponent_')) {
          const aiStyle = debateData.ai_style || "AI-Opponent";
          // Remove emoji prefix - we use icons now
          const cleanName = aiStyle.replace(/^🤖\s*/, '');
          setOpponentName(cleanName);
          
          // Extract icon based on AI character name
          const iconMap = {
            "Der Analyst": "/ai-analyst.png",
            "Der Empath": "/ai-empath.png",
            "Der Skeptiker": "/ai-skeptiker.png",
            "Der Pragmatiker": "/ai-pragmatiker.png",
            "Der Advocatus Diaboli": "/ai-advocatus.png"
          };
          
          // Find matching character name in aiStyle
          const matchedIcon = Object.keys(iconMap).find(key => aiStyle.includes(key));
          if (matchedIcon) {
            setOpponentIcon(iconMap[matchedIcon]);
          }
        } else {
          try {
            const sessionResponse = await axios.get(`${API}/session/${opponentSessionId}`);
            setOpponentName(sessionResponse.data.participant_name || 'Opponent');
            setOpponentIcon(null); // No icon for human opponents
          } catch (error) {
            console.error('Error fetching opponent session:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching debate:', error);
    }
  };

  // Update AI opponent name when ai_style changes
  useEffect(() => {
    if (debate?.is_ai_opponent && debate?.ai_style) {
      const aiName = debate.ai_style.startsWith('🤖') ? debate.ai_style : `🤖 ${debate.ai_style}`;
      setOpponentName(aiName);
      console.log('[AI Name Update]', aiName);
    }
  }, [debate?.ai_style]);

  // AI Auto-Play Logic
  useEffect(() => {
    console.log('[AI Auto-Play] Checking conditions...', {
      hasDebate: !!debate,
      isAIOpponent: debate?.is_ai_opponent,
      currentTurn,
      submitting,
      debateId,
      argumentCount: debateArguments?.length
    });
    
    if (!debate || !debate.is_ai_opponent) {
      console.log('[AI Auto-Play] Not an AI opponent debate');
      return;
    }
    
    const myRole = localStorage.getItem('participant_role');
    console.log('[AI Auto-Play] myRole:', myRole);
    
    const isMyTurn = (currentTurn === 'a' && myRole === 'protagonist_a') || 
                     (currentTurn === 'b' && myRole === 'protagonist_b');
    
    console.log('[AI Auto-Play] isMyTurn:', isMyTurn, 'currentTurn:', currentTurn);
    
    // If it's AI's turn (not my turn and turn is set) - trigger immediately
    if (!isMyTurn && currentTurn && !submitting) {
      const aiTurnRole = currentTurn; // 'a' or 'b'
      const aiSessionId = aiTurnRole === 'a' ? debate.session_a_id : debate.session_b_id;
      
      console.log('[AI Auto-Play] Checking AI turn...', {
        aiTurnRole,
        aiSessionId,
        startsWithAI: aiSessionId?.startsWith('ai_opponent_')
      });
      
      // Check if AI should play
      if (aiSessionId && aiSessionId.startsWith('ai_opponent_')) {
        console.log('[AI Auto-Play] ✓ AI is responding (backend handles generation)...');
        
        // Show AI status immediately - no artificial delay needed
        // Backend now triggers AI response automatically in parallel
        setAiStatus({ show: true, phase: 'generating', remaining: 0, total: 0 });
        
        // Start polling for AI response every 1 second
        const pollInterval = setInterval(async () => {
          try {
            console.log('[AI Auto-Play] Polling for AI response...');
            await fetchArguments();
            await fetchDebate();
            
            // Check if turn switched back (AI responded)
            const updatedDebate = await axios.get(`${API}/debate/${debateId}`);
            if (updatedDebate.data.current_turn === 'a') {
              console.log('[AI Auto-Play] ✅ AI has responded!');
              clearInterval(pollInterval);
              setAiStatus({ show: false, phase: 'thinking', remaining: 0, total: 0 });
            }
          } catch (error) {
            console.error('[AI Auto-Play] Polling error:', error);
          }
        }, 1000); // Poll every 1 second
        
        // Timeout after 30 seconds
        const timeout = setTimeout(() => {
          console.warn('[AI Auto-Play] Timeout waiting for AI response');
          clearInterval(pollInterval);
          setAiStatus({ show: false, phase: 'thinking', remaining: 0, total: 0 });
          toast.error('AI response timed out');
        }, 30000);
        
        return () => {
          clearInterval(pollInterval);
          clearTimeout(timeout);
        };
      } else {
        console.log('[AI Auto-Play] ✗ Not an AI session or wrong turn');
      }
    } else {
      console.log('[AI Auto-Play] Conditions not met:', { isMyTurn, currentTurn, submitting });
    }
  }, [currentTurn, debate, submitting, debateId, debateArguments?.length]);

  const fetchArguments = async () => {
    try {
      const response = await axios.get(`${API}/debate/${debateId}/arguments`);
      setDebateArguments(response.data);
    } catch (error) {
      console.error('Error fetching arguments:', error);
    }
  };

  const startMyTurn = () => {
    const myRole = localStorage.getItem('participant_role');
    const turn = myRole === 'protagonist_a' ? 'a' : 'b';
    setCurrentTurn(turn);
    setIsTimerRunning(true);
    
    // Total time will be started by backend on first argument submission
    // No need to start it locally anymore
  };

  const stopTimer = () => {
    setIsTimerRunning(false);
    setCurrentTurn(null);
  };

  const handleSplitTimerChange = (times) => {
    setThinkingTime(times.thinkingTime);
    setTypingTime(times.typingTime);
  };

  const submitArgument = async () => {
    if (!newArgument.trim()) {
      toast.error('Please enter an argument');
      return;
    }

    const sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      toast.error('Session not found');
      return;
    }

    // Stop timer when submitting
    const wasRunning = isTimerRunning;
    stopTimer();

    setSubmitting(true);
    
    // Check if this is the first argument (no arguments yet in debate)
    const isFirstArgument = !debate?.arguments || debate.arguments.length === 0;
    
    // Show "ANALYZE" animation for first argument
    if (isFirstArgument) {
      setAiStatus({ show: true, phase: 'analyzing', remaining: 0, total: 0 });
      console.log('[Submit] First argument - showing ANALYZE animation');
    }
    
    try {
      await axios.post(`${API}/debate/${debateId}/submit-argument`, {
        debate_id: debateId,
        session_id: sessionId,
        content: newArgument,
        thinking_time_seconds: thinkingTime,
        typing_time_seconds: typingTime
      });
      
      // Update backend with remaining time
      const myRole = localStorage.getItem('participant_role');
      const timeUpdate = myRole === 'protagonist_a' 
        ? { time_remaining_a: timeRemainingA }
        : { time_remaining_b: timeRemainingB };
      
      await axios.patch(`${API}/debate/${debateId}/time`, timeUpdate);
      
      // Reset per-argument timer for next argument (argument_time_limit already in seconds)
      if (myRole === 'protagonist_a') {
        setTimeRemainingA(debate.argument_time_limit); // Reset to full per-argument time
      } else {
        setTimeRemainingB(debate.argument_time_limit); // Reset to full per-argument time
      }
      
      setNewArgument('');
      setThinkingTime(0);
      setTypingTime(0);
      setHasAutoStarted(false);  // Reset for next argument
      
      toast.success('Argument submitted!');
      
      // Small delay to ensure backend has completed all updates
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Fetch updates to get new turn from backend
      await fetchArguments();
      await fetchDebate();  // This will update currentTurn from backend
      
      setIsTimerRunning(false);  // Stop current timer
      console.log('[Submit] Argument submitted, turn updated from backend');
      
    } catch (error) {
      console.error('Error submitting argument:', error);
      toast.error('Error submitting argument');
    } finally {
      setSubmitting(false);
    }
  };
  
  // ========================================
  // FUN FACTS FUNCTIONS - NEW LOGIC
  // ========================================
  
  // Load generic facts pool (once at start)
  const loadGenericFactsPool = async () => {
    try {
      console.log('[FunFacts] Loading generic facts pool from database...');
      const response = await axios.get(`${API}/debate/${debateId}/fun-facts-fast`);
      const facts = response.data.facts || [];
      console.log('[FunFacts] Loaded', facts.length, 'generic facts into pool');
      setGenericFactsPool(facts);
      return facts;
    } catch (error) {
      console.error('[FunFacts] Error loading generic facts pool:', error);
      return [];
    }
  };
  
  // Load thesis-specific facts (AI-generated, for 2nd+ arguments)
  const loadThesisSpecificFacts = async () => {
    try {
      console.log('[FunFacts] Loading THESIS-SPECIFIC facts from AI...');
      const response = await axios.get(`${API}/debate/${debateId}/fun-facts`);
      const facts = response.data.facts || [];
      console.log('[FunFacts] Loaded', facts.length, 'thesis-specific facts');
      setThesisSpecificFacts(facts);
      return facts;
    } catch (error) {
      console.error('[FunFacts] Error loading thesis-specific facts:', error);
      return [];
    }
  };
  
  // Get a random fact from the pool
  const getRandomFactFromPool = (pool) => {
    if (pool.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * pool.length);
    return pool[randomIndex];
  };
  
  const toggleFunFactsCollapse = () => {
    const newCollapsed = !funFactsCollapsed;
    setFunFactsCollapsed(newCollapsed);
    // Save preference to localStorage
    localStorage.setItem('funFactsCollapsed', newCollapsed.toString());
  };
  
  // Load generic facts pool when debate starts
  useEffect(() => {
    if (debate && genericFactsPool.length === 0) {
      console.log('[FunFacts] Debate started - loading generic facts pool...');
      loadGenericFactsPool();
    }
  }, [debate?.id]);
  
  // NEW LOGIC: Load fact while typing first argument
  useEffect(() => {
    if (newArgument.length > 0 && isFirstArgument && genericFactsPool.length > 0 && funFacts.length === 0) {
      console.log('[FunFacts] User is typing first argument - loading random generic fact...');
      const randomFact = getRandomFactFromPool(genericFactsPool);
      if (randomFact) {
        setFunFacts([randomFact]); // Only ONE fact
        console.log('[FunFacts] ✅ Loaded random generic fact for first argument');
      }
    }
  }, [newArgument, isFirstArgument, genericFactsPool]);
  
  // Load localStorage preference once at start
  useEffect(() => {
    if (!hasLoadedPreference.current) {
      // TEMPORARY FIX: Always start with box expanded (not collapsed)
      // const collapsed = localStorage.getItem('funFactsCollapsed') === 'true';
      const collapsed = false; // Force expanded
      setFunFactsCollapsed(collapsed);
      hasLoadedPreference.current = true;
      console.log('[FunFacts] Force starting with expanded box (collapsed=false)');
    }
  }, []);
  
  // Show/hide fun facts based on AI thinking state
  useEffect(() => {
    const isAIThinking = submitting || (debate?.is_ai_opponent && currentTurn === 'b');
    
    console.log('[FunFacts] AI Thinking Check:', { 
      submitting, 
      currentTurn, 
      isAIThinking, 
      isFirstArgument,
      funFactsLength: funFacts.length,
      thesisSpecificLength: thesisSpecificFacts.length,
      genericPoolLength: genericFactsPool.length
    });
    
    if (isAIThinking) {
      console.log('[FunFacts] ✓ AI is thinking - showing facts NOW!');
      
      // ALWAYS show the box when AI is thinking
      setShowFunFacts(true);
      
      if (isFirstArgument) {
        // FIRST ARGUMENT: Show the preloaded generic fact
        console.log('[FunFacts] First argument - showing generic fact');
        if (funFacts.length > 0) {
          setLoadingFacts(false);
          console.log('[FunFacts] ✅ Generic fact ready to display');
        } else {
          // If fact not loaded yet, show loading
          setLoadingFacts(true);
          console.log('[FunFacts] ⚠️ No fact loaded yet, showing loading...');
        }
        
        // Start loading thesis-specific facts in background for next time
        console.log('[FunFacts] Starting to load thesis-specific facts in background...');
        loadThesisSpecificFacts();
        
        // Mark that first argument is done
        setIsFirstArgument(false);
      } else {
        // SECOND+ ARGUMENT: Show thesis-specific facts
        console.log('[FunFacts] Second+ argument - using thesis-specific facts');
        
        if (thesisSpecificFacts.length > 0) {
          // Use thesis-specific facts
          const randomFact = getRandomFactFromPool(thesisSpecificFacts);
          if (randomFact) {
            setFunFacts([randomFact]); // Only ONE fact
            setLoadingFacts(false);
            console.log('[FunFacts] ✅ Showing thesis-specific fact');
          }
        } else {
          // Fallback: Use generic facts if no thesis-specific available
          console.log('[FunFacts] No thesis-specific facts available - using generic fallback');
          const randomFact = getRandomFactFromPool(genericFactsPool);
          if (randomFact) {
            setFunFacts([randomFact]); // Only ONE fact
            setLoadingFacts(false);
            console.log('[FunFacts] ✅ Showing generic fallback fact');
          } else {
            setLoadingFacts(true);
            console.log('[FunFacts] ⚠️ No facts available, showing loading...');
          }
        }
      }
    } else if (showFunFacts && !isAIThinking) {
      // AI finished - hide after short delay
      console.log('[FunFacts] AI finished, hiding in 2 seconds...');
      const hideTimer = setTimeout(() => {
        console.log('[FunFacts] Hiding now');
        setShowFunFacts(false);
      }, 2000);
      return () => clearTimeout(hideTimer);
    }
  }, [submitting, currentTurn, debate?.is_ai_opponent, isFirstArgument, thesisSpecificFacts, genericFactsPool]);
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (funFactsTimerRef.current) {
        clearTimeout(funFactsTimerRef.current);
      }
    };
  }, []);

  // ========================================
  // TEXT-TO-SPEECH FUNCTIONS
  // ========================================
  
  const playArgument = (argumentId, text) => {
    // Stop any currently playing speech
    window.speechSynthesis.cancel();
    
    if (playingArgumentId === argumentId) {
      // If clicking the same argument, stop it
      setPlayingArgumentId(null);
      return;
    }
    
    // Detect language from debate
    const lang = debate?.language === 'de' ? 'de-DE' : 
                 debate?.language === 'en' ? 'en-US' :
                 debate?.language === 'fr' ? 'fr-FR' :
                 debate?.language === 'es' ? 'es-ES' :
                 debate?.language === 'it' ? 'it-IT' : 'de-DE';
    
    // Create speech utterance
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = ttsSpeed;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    // Event handlers
    utterance.onstart = () => {
      setPlayingArgumentId(argumentId);
    };
    
    utterance.onend = () => {
      setPlayingArgumentId(null);
    };
    
    utterance.onerror = (event) => {
      // Only show error for actual errors, not for cancel/interrupt events
      if (event.error !== 'canceled' && event.error !== 'interrupted') {
        console.error('Speech synthesis error:', event);
        toast.error('Fehler beim Vorlesen');
      }
      setPlayingArgumentId(null);
    };
    
    // Start speaking
    window.speechSynthesis.speak(utterance);
  };
  
  const stopAllSpeech = () => {
    window.speechSynthesis.cancel();
    setPlayingArgumentId(null);
  };
  
  // Stop speech when component unmounts or debate changes
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, [debateId]);

  const generateInsights = async () => {
    setGeneratingInsights(true);
    try {
      await axios.post(`${API}/debate/${debateId}/generate-insights`);
      await fetchDebate(); // Refresh debate data to get insights
      toast.success('Insights generated!');
      setShowInsightsModal(true); // Open insights modal
    } catch (error) {
      console.error('Error generating insights:', error);
      toast.error('Failed to generate insights');
    } finally {
      setGeneratingInsights(false);
    }
  };

  const exportArguments = async () => {
    const sessionId = localStorage.getItem('session_id');
    try {
      const response = await axios.get(`${API}/debate/${debateId}/export/${sessionId}`);
      const blob = new Blob([response.data.content], { type: 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('Arguments exported!');
    } catch (error) {
      console.error('Error exporting arguments:', error);
      toast.error('Failed to export arguments');
    }
  };

  const completeDebate = async () => {
    try {
      await axios.post(`${API}/debate/${debateId}/complete`);
      toast.success('Debate completed!');
      
      // Check if debate was started as guest
      const startedAsGuest = localStorage.getItem('debate_started_as_guest');
      const sessionToken = localStorage.getItem('user_session_token');
      
      // DEBUG LOG
      console.log('🔍 COMPLETE DEBUG:', {
        startedAsGuest,
        sessionToken: sessionToken ? 'exists' : 'null',
        willNavigateTo: startedAsGuest === 'true' ? '/' : (sessionToken ? '/archive' : '/')
      });
      
      // Clean up guest flag
      localStorage.removeItem('debate_started_as_guest');
      
      if (startedAsGuest === 'true') {
        // User explicitly chose to debate as guest - go to landing page
        console.log('✅ Navigating to / (guest mode)');
        navigate('/');
      } else if (sessionToken) {
        // Registered user who logged in before debate - go to archive
        console.log('✅ Navigating to /archive (registered user)');
        navigate('/archive');
      } else {
        // Guest user - go to landing page
        console.log('✅ Navigating to / (no token)');
        navigate('/');
      }
    } catch (error) {
      console.error('Error completing debate:', error);
      toast.error('Error completing debate');
    }
  };

  const getScoreBadge = (score) => {
    if (!score) return null;
    const total = score.total_score;
    const variant = total > 0 ? 'default' : total < 0 ? 'destructive' : 'secondary';
    return (
      <Badge variant={variant} className="ml-2">
        {total > 0 ? '+' : ''}{total}
      </Badge>
    );
  };

  const getScoreTags = (tags) => {
    if (!tags || tags.length === 0) return null;
    return tags.map((tag, idx) => (
      <Badge key={idx} variant="outline" className="mr-1 text-xs">
        {tag}
      </Badge>
    ));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getOpponentLastArgument = () => {
    const myRole = localStorage.getItem('participant_role');
    const opponentRole = myRole === 'protagonist_a' ? 'protagonist_b' : 'protagonist_a';
    const opponentArgs = debateArguments.filter(arg => arg.participant_role === opponentRole);
    return opponentArgs.length > 0 ? opponentArgs[opponentArgs.length - 1] : null;
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const scrollToArgument = (argumentId) => {
    const element = document.getElementById(`argument-${argumentId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // New functions for redesign
  const handleStopDebate = async () => {
    if (!window.confirm('Are you sure you want to stop the debate? Your opponent will be declared the winner.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/debate/${debateId}/complete`, {
        completion_reason: 'opponent_conceded',
        winner: myRole === 'protagonist_a' ? 'b' : 'a'
      });
      toast.info('Debate ended - Opponent wins');
      
      // Check if debate was started as guest
      const startedAsGuest = localStorage.getItem('debate_started_as_guest');
      const sessionToken = localStorage.getItem('user_session_token');
      
      // DEBUG LOG
      console.log('🔍 STOP DEBUG:', {
        startedAsGuest,
        sessionToken: sessionToken ? 'exists' : 'null',
        willNavigateTo: startedAsGuest === 'true' ? '/' : (sessionToken ? '/archive' : '/')
      });
      
      // Clean up guest flag
      localStorage.removeItem('debate_started_as_guest');
      
      if (startedAsGuest === 'true') {
        // User explicitly chose to debate as guest - go to landing page
        console.log('✅ Navigating to / (guest mode)');
        navigate('/');
      } else if (sessionToken) {
        // Registered user who logged in before debate - go to archive
        console.log('✅ Navigating to /archive (registered user)');
        navigate('/archive');
      } else {
        // Guest user - go to landing page
        console.log('✅ Navigating to / (no token)');
        navigate('/');
      }
    } catch (error) {
      console.error('Error stopping debate:', error);
      toast.error('Failed to stop debate');
    }
  };

  // Handler for mobile context button [C]
  const handleMobileContext = () => {
    // Find last human argument (from current user)
    const mySessionId = localStorage.getItem('session_id');
    const myArguments = debateArguments.filter(arg => arg.session_id === mySessionId);
    
    if (myArguments.length > 0) {
      const lastArg = myArguments[myArguments.length - 1];
      if (lastArg.moderation_result) {
        setMobileContextData(lastArg.moderation_result);
        setShowMobileContext(true);
      } else {
        toast.info('Noch keine Analyse verfügbar');
      }
    } else {
      toast.info('Noch kein Argument eingereicht');
    }
  };

  const toggleContextButton = (argumentId) => {
    setActiveContextButton(prev => ({
      ...prev,
      [argumentId]: !prev[argumentId]
    }));
  };

  const startMicrophoneInput = async () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = debate.language === 'de' ? 'de-DE' : 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsRecording(true);
      if (!hasAutoStarted && !isTimerRunning) {
        setHasAutoStarted(true);
        startMyTurn();
      }
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + ' ';
        }
      }
      if (finalTranscript) {
        setNewArgument(prev => prev + finalTranscript);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
      toast.error('Speech recognition error: ' + event.error);
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognition.start();
    
    // Store recognition instance to stop it later
    window.currentRecognition = recognition;
  };

  const stopMicrophoneInput = () => {
    if (window.currentRecognition) {
      window.currentRecognition.stop();
      window.currentRecognition = null;
    }
    setIsRecording(false);
  };

  const handleModeToggle = (mode) => {
    if (mode === 'microphone' && inputMode === 'keyboard') {
      setInputMode('microphone');
      startMicrophoneInput();
    } else if (mode === 'keyboard' && inputMode === 'microphone') {
      stopMicrophoneInput();
      setInputMode('keyboard');
    }
  };

  if (!debate) return <div className="page-container">Loading...</div>;

  // Define myRole and isMyTurn for mobile and desktop use - MOVED TO TOP
  const myRole = localStorage.getItem('participant_role');
  const isMyTurn = (myRole === 'protagonist_a' && currentTurn === 'a') || 
                   (myRole === 'protagonist_b' && currentTurn === 'b');

  const opponentLastArg = getOpponentLastArgument();
  const opponentRole = myRole === 'protagonist_a' ? 'protagonist_b' : 'protagonist_a';
  const myName = localStorage.getItem('participant_name') || 'You';
  
  // Calculate total scores for both protagonists
  const myScore = debateArguments
    .filter(arg => arg.participant_role === myRole && arg.moderation_result?.score)
    .reduce((sum, arg) => sum + (arg.moderation_result.score.total_score || 0), 0);
  
  const opponentScore = debateArguments
    .filter(arg => arg.participant_role === opponentRole && arg.moderation_result?.score)
    .reduce((sum, arg) => sum + (arg.moderation_result.score.total_score || 0), 0);

  // Calculate scores for display
  const scores = {
    A: debate?.score_a || 0,
    B: debate?.score_b || 0
  };

  // Mobile Render Function - Tablet Portrait Layout (1080px)
  const renderMobile = () => {
    // Get last argument's score for the +2 button display
    const myArguments = debateArguments.filter(arg => arg.session_id === localStorage.getItem('session_id'));
    const lastScore = myArguments.length > 0 ? (myArguments[myArguments.length - 1].moderation_result?.score?.total_score || 0) : 0;
    
    return (
      <div 
        className="mobile-debate-room"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}${designConfig.debate_bg_mobile})`
        }}
      >
        {/* Fixed Header with Graphics */}
        <div 
          className="mobile-header"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}${designConfig.debate_header_mobile})`
          }}
        >
          {/* Invisible Home Button over Logo - Mobile proportional positioning */}
          <button
            className="mobile-logo-home-button"
            onClick={() => navigate('/')}
            aria-label="Zurück zur Startseite"
            title="Zurück zur Startseite"
          />
          
          {/* Scorebox Left */}
          <div 
            className="mobile-score-box-left"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/${
                scores.A >= 0 ? 'Square-plusScore_V03.png' : 'Square-minusScore_V03.png'
              })`
            }}
          >
            {scores.A}
          </div>
          
          {/* Timer Box Left */}
          <div 
            className="mobile-timer-box-left"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/${
                currentTurn === 'a' && isTimerRunning ? 'Square-TimeRunning_V03.png' : 'Square-Timestill_V03.png'
              })`
            }}
          >
            {formatTime(timeRemainingA)}
          </div>
          
          {/* Scorebox Right */}
          <div 
            className="mobile-score-box-right"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/${
                scores.B >= 0 ? 'Square-plusScore_V03.png' : 'Square-minusScore_V03.png'
              })`
            }}
          >
            {scores.B}
          </div>
          
          {/* Timer Box Right */}
          <div 
            className="mobile-timer-box-right"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/${
                currentTurn === 'b' && isTimerRunning ? 'Square-TimeRunning_V03.png' : 'Square-Timestill_V03.png'
              })`
            }}
          >
            {formatTime(timeRemainingB)}
          </div>
          
          {/* Total Time Counter */}
          <div className="mobile-total-time">
            {formatTime(totalTimeRemaining)}
          </div>
        </div>

        {/* AI Status Bar - Shows AI thinking/generating progress */}
        {aiStatus.show && (
          <div className="mobile-ai-status-container">
            <div className="mobile-ai-status-text">
              {aiStatus.phase === 'analyzing'
                ? <span className="analyze-animation">ANALYZE</span>
                : aiStatus.phase === 'thinking' 
                  ? `🤖 AI is thinking... ${Math.ceil(aiStatus.remaining / 1000)}s`
                  : '⚡ Generating response...'}
            </div>
            <div className="mobile-ai-status-bar">
              <div 
                className={`mobile-ai-status-bar-fill ${aiStatus.phase === 'thinking' ? 'countdown' : 'indeterminate'}`}
                style={aiStatus.phase === 'thinking' 
                  ? { width: `${(aiStatus.remaining / aiStatus.total) * 100}%` }
                  : {}}
              />
            </div>
          </div>
        )}
        
        {/* Thesis Box - Outside header, absolute to main container */}
        <div className="mobile-thesis-box">
          {debate?.thesis || 'Lade These...'}
        </div>

        {/* Mobile Feedback Modal */}
        <FeedbackModal 
          isOpen={feedbackModalOpen}
          onClose={() => {
            setFeedbackModalOpen(false);
            setFeedbackArgument(null);
          }}
          argument={feedbackArgument}
          debateId={debate?.id}
        />

        {/* Mobile Context Analysis Modal - Full detailed analysis */}
        {showMobileContext && mobileContextData && (
          <div className="mobile-context-overlay" onClick={() => setShowMobileContext(false)}>
            <div className="mobile-context-modal" onClick={(e) => e.stopPropagation()}>
              <div className="mobile-context-header">
                <div className="mobile-context-title">Argument Analyse</div>
                <button 
                  className="mobile-context-close"
                  onClick={() => setShowMobileContext(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="mobile-context-content">
                {/* Summary - Always visible */}
                <div className="mobile-context-summary">
                  <strong>Summary:</strong> {mobileContextData.summary || 'Keine Zusammenfassung verfügbar'}
                </div>
                
                {/* Detailed AI Feedback - Collapsible */}
                {mobileContextData.ai_feedback && (
                  <details className="mobile-context-details">
                    <summary>Detaillierte AI Bewertung anzeigen</summary>
                    <div className="mobile-context-details-content">
                      <p style={{ whiteSpace: 'pre-wrap' }}>{mobileContextData.ai_feedback}</p>
                    </div>
                  </details>
                )}
                
                {/* Analysis Details - Collapsible */}
                {(mobileContextData.fact_check_result || mobileContextData.logic_analysis || 
                  mobileContextData.relevance_analysis || mobileContextData.feasibility_analysis) && (
                  <details className="mobile-context-details">
                    <summary>Analyse Details anzeigen</summary>
                    <div className="mobile-context-details-content">
                      {mobileContextData.fact_check_result && (
                        <div className="mobile-context-details-item">
                          <strong>Faktencheck:</strong>
                          <p>{mobileContextData.fact_check_result}</p>
                        </div>
                      )}
                      {mobileContextData.logic_analysis && (
                        <div className="mobile-context-details-item">
                          <strong>Logik Analyse:</strong>
                          <p>{mobileContextData.logic_analysis}</p>
                        </div>
                      )}
                      {mobileContextData.relevance_analysis && (
                        <div className="mobile-context-details-item">
                          <strong>Relevanz:</strong>
                          <p>{mobileContextData.relevance_analysis}</p>
                        </div>
                      )}
                      {mobileContextData.feasibility_analysis && (
                        <div className="mobile-context-details-item">
                          <strong>Durchführbarkeit:</strong>
                          <p>{mobileContextData.feasibility_analysis}</p>
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Chat Area */}
        <div className="mobile-chat-area">
          {debateArguments.map((arg, index) => {
            const isHuman = arg.session_id === localStorage.getItem('session_id');
            const score = arg.moderation_result?.score?.total_score || 0;
            const hasAnalysis = arg.moderation_result && (
              arg.moderation_result.summary || 
              arg.moderation_result.ai_feedback ||
              arg.moderation_result.fact_check_result ||
              arg.moderation_result.logic_analysis
            );
            
            return (
              <div 
                key={index} 
                className={`mobile-argument-item ${isHuman ? 'human' : 'ai'}`}
              >
                <div className="mobile-argument-content">{arg.content}</div>
                <div className="mobile-argument-meta">
                  {hasAnalysis ? (
                    <button 
                      className="mobile-score-badge mobile-score-badge-clickable"
                      onClick={() => {
                        setMobileContextData(arg.moderation_result);
                        setShowMobileContext(true);
                      }}
                      title="Klicken für Analyse"
                    >
                      {score > 0 ? '+' : ''}{score} {score === 1 || score === -1 ? 'Punkt' : 'Punkte'}
                    </button>
                  ) : (
                    <span className="mobile-score-badge">
                      {score > 0 ? '+' : ''}{score} {score === 1 || score === -1 ? 'Punkt' : 'Punkte'}
                    </span>
                  )}
                  <span>•</span>
                  <span>{new Date(arg.timestamp).toLocaleTimeString('de-DE', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}</span>
                  <span>•</span>
                  <button 
                    className="mobile-feedback-icon-btn"
                    onClick={() => {
                      setFeedbackArgument(arg);
                      setFeedbackModalOpen(true);
                    }}
                    title="Feedback geben"
                  >
                    💬
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Fixed Input Area - 200px from bottom */}
        <div className={`mobile-input-area ${newArgument ? 'keyboard-active' : ''}`}>
          <div className="mobile-input-container">
            {/* Textarea - volle Breite oben */}
            <textarea
              ref={mobileTextareaRef}
              className="mobile-textarea"
              value={newArgument}
              onChange={(e) => setNewArgument(e.target.value)}
              onFocus={() => {
                // Textarea fokussiert - visualViewport Event-Handler wird Position anpassen
              }}
              onBlur={() => {
                // Textarea nicht mehr fokussiert - Position wird durch visualViewport zurückgesetzt
              }}
              placeholder="Dein Argument..."
              disabled={submitting || !isMyTurn}
            />
            
            {/* Buttons Row - Buttons in einer Reihe unter Textarea */}
            <div className="mobile-input-buttons-row">
              {/* 1. Keyboard Button */}
              <button 
                className={`mobile-input-btn mobile-keyboard-btn ${inputMode === 'keyboard' ? 'active' : ''}`}
                onClick={() => handleModeToggle('keyboard')}
                title="Tastatur Eingabe"
              >
                <Keyboard className="mobile-btn-icon" />
              </button>
              
              {/* 2. Microphone Button */}
              <button 
                className={`mobile-input-btn mobile-microphone-btn ${inputMode === 'microphone' ? 'active' : ''}`}
                onClick={() => handleModeToggle('microphone')}
                title="Sprach Eingabe"
              >
                <Mic className="mobile-btn-icon" />
              </button>
              
              {/* 3. Score Button +2 */}
              <button 
                className="mobile-input-btn mobile-score-btn"
                title="Letzte Punktzahl"
                disabled
              >
                <div className="mobile-score-btn-value">
                  {lastScore > 0 ? '+' : ''}{lastScore}
                </div>
              </button>
              
              {/* 4. Context Button C */}
              <button 
                className="mobile-input-btn mobile-context-btn"
                onClick={handleMobileContext}
                title="Argument Analyse anzeigen"
              >
                C
              </button>
              
              {/* 5. STOP Button - Well Argued */}
              <button 
                className="mobile-input-btn mobile-stop-btn"
                onClick={handleStopDebate}
                title="Debatte beenden"
              >
                <div className="mobile-stop-btn-text">STOP</div>
                <div className="mobile-stop-btn-subtext">Well Argued</div>
              </button>
              
              {/* 6. Submit Button SENDEN */}
              <button 
                className="mobile-input-btn mobile-submit-btn"
                onClick={submitArgument}
                disabled={submitting || !newArgument.trim() || !isMyTurn}
              >
                SENDEN
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Desktop Render - original return
  const renderDesktop = () => {
    return (
    <div 
      className="debate-room-new"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}${designConfig.debate_bg_desktop})`
      }}
    >
      {/* Debate Content Wrapper - Fixed 1920px, always centered */}
      <div className="debate-content-wrapper">
        {/* Combined Header + THESIS - New Full Header */}
        <div 
          className="debate-header-full"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}${designConfig.debate_header_desktop})`
          }}
        >
        {/* Invisible Home Button over Logo - x=222 to 575, y=100 to 178 */}
        <button
          className="logo-home-button"
          onClick={() => navigate('/')}
          aria-label="Zurück zur Startseite"
          title="Zurück zur Startseite"
        />
        
        {/* Score Box Left - x=50, y=45 */}
        <div 
          className="header-score-box-left"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/${
              myRole === 'protagonist_a' ?
                (myScore >= 0 ? 'Square-plusScore_V03.png' : 'Square-minusScore_V03.png') :
                (opponentScore >= 0 ? 'Square-plusScore_V03.png' : 'Square-minusScore_V03.png')
            })`
          }}
        >
          <div className="header-score-value">
            {myRole === 'protagonist_a' ? myScore : opponentScore}
          </div>
        </div>

        {/* Response Timer Box A - x=180, y=45 */}
        <div 
          className={`header-timer-box-left ${currentTurn === 'a' ? 'running' : 'still'}`}
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/${
              currentTurn === 'a' ? 'Square-TimeRunning_V03.png' : 'Square-Timestill_V03.png'
            })`
          }}
        >
          <div className="header-timer-time">{formatTime(timeRemainingA)}</div>
        </div>

        {/* Protagonist A Name - x=260, y=45 (left aligned) - Always Human */}
        <div className="protagonist-name-left">
          {debate?.is_ai_opponent 
            ? myName  // If AI opponent, always show human name on left
            : (myRole === 'protagonist_a' ? myName : opponentName)
          }
        </div>

        {/* Total Time Center - x=960, y=45 */}
        <div className="header-total-time">
          <div className="header-total-time-value">{formatTime(totalTimeRemaining)}</div>
        </div>

        {/* Protagonist B Name - x=1635, y=45 (right aligned) - AI or Human */}
        <div className="protagonist-name-right" style={{ position: 'relative' }}>
          {opponentIcon && (
            <img 
              src={opponentIcon} 
              alt="AI Character" 
              style={{
                width: '64px',
                height: '64px',
                marginRight: '12px',
                marginLeft: '-40px',
                verticalAlign: 'middle',
                display: 'inline-block'
              }}
            />
          )}
          {debate?.is_ai_opponent 
            ? (() => {
                // Split AI name to put model name on second line
                const fullName = opponentName;
                // Check if name contains bracketed model name like [ANTHROPIC], [OPENAI], etc.
                const bracketMatch = fullName.match(/^(.*?)(\[.*?\])$/);
                if (bracketMatch) {
                  // Name has format like "Der Advocatus Diaboli [ANTHROPIC]"
                  const characterName = bracketMatch[1].trim();
                  const modelName = bracketMatch[2];
                  return (
                    <>
                      {characterName}<br />{modelName}
                    </>
                  );
                } else {
                  // No bracket, just show full name
                  return fullName;
                }
              })()
            : (myRole === 'protagonist_b' ? myName : opponentName)
          }
        </div>

        {/* Response Timer Box B - x=1715, y=45 */}
        <div 
          className={`header-timer-box-right ${currentTurn === 'b' ? 'running' : 'still'}`}
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/${
              currentTurn === 'b' ? 'Square-TimeRunning_V03.png' : 'Square-Timestill_V03.png'
            })`
          }}
        >
          <div className="header-timer-time">{formatTime(timeRemainingB)}</div>
        </div>

        {/* Score Box Right - x=1870, y=45 */}
        <div 
          className="header-score-box-right"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/${
              myRole === 'protagonist_b' ?
                (myScore >= 0 ? 'Square-plusScore_V03.png' : 'Square-minusScore_V03.png') :
                (opponentScore >= 0 ? 'Square-plusScore_V03.png' : 'Square-minusScore_V03.png')
            })`
          }}
        >
          <div className="header-score-value">
            {myRole === 'protagonist_b' ? myScore : opponentScore}
          </div>
        </div>

        {/* THESIS Label - x=960, y=165 */}
        <div className="thesis-label-full">THESIS</div>

        {/* Thesis Text Content - positioned within full header */}
        <div className="thesis-content-full">{debate.thesis}</div>
        </div> {/* End debate-header-full */}

        {/* Arguments Container */}
        <div className="arguments-container">
        {debateArguments.map((arg, idx) => {
          const isOpponent = arg.participant_role === opponentRole;
          const argScore = arg.moderation_result?.score?.total_score || 0;
          
          return (
            <div key={arg.id}>
              {isOpponent ? (
                /* Opponent Argument - Black Box */
                <div className="argument-box-opponent">
                  <div className="argument-content" onClick={() => setExpandedArgument(arg)}>
                    {arg.content}
                  </div>
                  
                  <div className="argument-controls">
                    <div className={`score-button ${argScore >= 0 ? 'positive' : 'negative'}`}>
                      {argScore > 0 ? '+' : ''}{argScore}
                    </div>
                    <button 
                      className={`context-button ${activeContextButton[arg.id] ? 'active' : ''}`}
                      onClick={() => toggleContextButton(arg.id)}
                    >
                      C
                    </button>
                    <button
                      className={`tts-button ${playingArgumentId === arg.id ? 'playing' : ''}`}
                      onClick={() => playArgument(arg.id, arg.content)}
                      title="Argument vorlesen"
                    >
                      {playingArgumentId === arg.id ? '⏸️' : '🔊'}
                    </button>
                  </div>
                  
                  <div className="argument-author">{opponentName}</div>
                </div>
              ) : (
                /* Own Argument - White Box */
                <div className="argument-box-own">
                  <div className="argument-content">
                    {arg.content}
                  </div>
                  
                  <div className="argument-controls">
                    <div className={`score-button ${argScore >= 0 ? 'positive' : 'negative'}`}>
                      {argScore > 0 ? '+' : ''}{argScore}
                    </div>
                    <button 
                      className={`context-button ${activeContextButton[arg.id] ? 'active' : ''}`}
                      onClick={() => toggleContextButton(arg.id)}
                    >
                      C
                    </button>
                    <button
                      className={`tts-button ${playingArgumentId === arg.id ? 'playing' : ''}`}
                      onClick={() => playArgument(arg.id, arg.content)}
                      title="Argument vorlesen"
                    >
                      {playingArgumentId === arg.id ? '⏸️' : '🔊'}
                    </button>
                  </div>
                  
                  <div className="argument-author">{myName}</div>
                </div>
              )}
              
              {/* AI Validation - Shown when Context button is active */}
              {activeContextButton[arg.id] && arg.moderation_result && (
                <div className="ai-validation-panel">
                  <div style={{ fontFamily: 'Abel', fontSize: '14px', lineHeight: '1.6' }}>
                    <p style={{ letterSpacing: '0.5px' }}>
                      <strong style={{ letterSpacing: '1px' }}>Summary:</strong> {arg.moderation_result.summary || 'No summary available'}
                    </p>
                    
                    {arg.moderation_result.ai_feedback && (
                      <details style={{ marginTop: '15px' }}>
                        <summary style={{ 
                          cursor: 'pointer', 
                          fontWeight: 'bold', 
                          fontFamily: 'Bebas Neue', 
                          fontSize: '16px',
                          letterSpacing: '1px'
                        }}>
                          View Detailed AI Feedback
                        </summary>
                        <div style={{ marginTop: '10px', paddingLeft: '15px', whiteSpace: 'pre-wrap', letterSpacing: '0.5px' }}>
                          <p>{arg.moderation_result.ai_feedback}</p>
                        </div>
                      </details>
                    )}

                    {(arg.moderation_result.fact_check_result || arg.moderation_result.logic_analysis || 
                      arg.moderation_result.relevance_analysis || arg.moderation_result.feasibility_analysis) && (
                      <details style={{ marginTop: '15px' }}>
                        <summary style={{ 
                          cursor: 'pointer', 
                          fontWeight: 'bold', 
                          fontFamily: 'Bebas Neue', 
                          fontSize: '16px',
                          letterSpacing: '1px'
                        }}>
                          View Analysis Details
                        </summary>
                        <div style={{ marginTop: '10px', paddingLeft: '15px', letterSpacing: '0.5px' }}>
                          {arg.moderation_result.fact_check_result && (
                            <div style={{ marginBottom: '10px' }}>
                              <strong style={{ letterSpacing: '0.8px' }}>Fact Check:</strong> {arg.moderation_result.fact_check_result}
                            </div>
                          )}
                          {arg.moderation_result.logic_analysis && (
                            <div style={{ marginBottom: '10px' }}>
                              <strong style={{ letterSpacing: '0.8px' }}>Logic Analysis:</strong> {arg.moderation_result.logic_analysis}
                            </div>
                          )}
                          {arg.moderation_result.relevance_analysis && (
                            <div style={{ marginBottom: '10px' }}>
                              <strong style={{ letterSpacing: '0.8px' }}>Relevance:</strong> {arg.moderation_result.relevance_analysis}
                            </div>
                          )}
                          {arg.moderation_result.feasibility_analysis && (
                            <div style={{ marginBottom: '10px' }}>
                              <strong style={{ letterSpacing: '0.8px' }}>Feasibility:</strong> {arg.moderation_result.feasibility_analysis}
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                  
                  {/* Feedback Button - nur für eigene Argumente */}
                  {arg.participant_role === myRole && (
                    <button
                      onClick={() => {
                        setFeedbackArgument(arg);
                        setFeedbackModalOpen(true);
                      }}
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontFamily: 'Abel',
                        fontWeight: '600',
                        transition: 'transform 0.2s',
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      📝 Feedback zur Bewertung
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div> {/* End debate-content-wrapper */}
      
      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => {
          setFeedbackModalOpen(false);
          setFeedbackArgument(null);
        }}
        argument={feedbackArgument}
        debateId={debateId}
      />

      {/* Desktop AI Status Bar - Shows AI thinking/generating progress */}
      {aiStatus.show && !isMobile && (
        <div className="desktop-ai-status-container">
          <div className="desktop-ai-status-text">
            {aiStatus.phase === 'analyzing'
              ? <span className="analyze-animation">ANALYZE</span>
              : aiStatus.phase === 'thinking' 
                ? `🤖 AI is thinking... ${Math.ceil(aiStatus.remaining / 1000)}s`
                : '⚡ Generating response...'}
          </div>
          <div className="desktop-ai-status-bar">
            <div 
              className={`desktop-ai-status-bar-fill ${aiStatus.phase === 'thinking' ? 'countdown' : 'indeterminate'}`}
              style={aiStatus.phase === 'thinking' 
                ? { width: `${(aiStatus.remaining / aiStatus.total) * 100}%` }
                : {}}
            />
          </div>
        </div>
      )}

      {/* Input Area Container - wraps fun facts + input section */}
      <div className="input-area-container">
        {/* Fun Facts Box - anchored to input section (Desktop only) */}
        {showFunFacts && !funFactsCollapsed && (
          <div className="desktop-fun-facts-box">
            <div className="desktop-fun-facts-header">
              <span className="desktop-fun-facts-title">Unnützes Wissen</span>
              <button 
                className="desktop-fun-facts-collapse-btn"
                onClick={toggleFunFactsCollapse}
                title="Minimieren"
              >
                ✕
              </button>
            </div>
            <div className="desktop-fun-facts-content">
              {loadingFacts || funFacts.length === 0 ? (
                <div className="desktop-fun-facts-loading">Lade Fakten...</div>
              ) : (
                <div className="desktop-fun-facts-text">
                  {funFacts[currentFactIndex] || 'Kein Fakt verfügbar'}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Input Section */}
        <div className="input-section">
          {/* Fun Facts Toggle Button - ALWAYS visible, left edge of input box */}
          <button 
            className="fun-facts-toggle-btn"
            onClick={toggleFunFactsCollapse}
            title={funFactsCollapsed ? "Unnützes Wissen anzeigen" : "Unnützes Wissen verbergen"}
          >
            i
          </button>
          
          <textarea
            className="input-textarea"
            value={newArgument}
            onChange={(e) => {
              const newValue = e.target.value;
              const wasEmpty = newArgument.length === 0;
              setNewArgument(newValue);
              
              // Auto-start timer ONLY ONCE
              if (!hasAutoStarted && !isTimerRunning && wasEmpty && newValue.length > 0) {
                console.log('Auto-starting timer (first keystroke)...');
                setHasAutoStarted(true);
                startMyTurn();
              }
            }}
            placeholder="Enter your argument here..."
            disabled={submitting || inputMode === 'microphone'}
          />

          <div className="controls-row">
            {/* Left: Insights & Export */}
            <div className="controls-left">
              <button 
                className="icon-button" 
                onClick={generateInsights}
                disabled={generatingInsights}
                title="Generate Insights"
              >
                <Lightbulb />
              </button>
              <button 
                className="icon-button" 
                onClick={exportArguments}
                title="Export Arguments"
              >
                <Download />
              </button>
            </div>

            {/* Center: Input Mode Toggle */}
            <div className="input-mode-toggle">
              <button 
                className={`mode-button ${inputMode === 'keyboard' ? 'active' : ''}`}
                onClick={() => handleModeToggle('keyboard')}
                title="Keyboard Input"
              >
                <Keyboard />
                <span>Keyboard</span>
              </button>
              <button 
                className={`mode-button ${inputMode === 'microphone' ? 'active' : ''}`}
                onClick={() => handleModeToggle('microphone')}
                title="Microphone Input"
              >
                <Mic />
                <span>Microphone</span>
              </button>
              {isRecording && (
                <div className="audio-amplitude" />
              )}
            </div>

            {/* Right: Stop & Submit Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
              {/* STOP Button (30px gap before Submit) */}
              <button className="stop-button" onClick={handleStopDebate}>
                <div className="stop-button-title">STOP</div>
                <div className="stop-button-subtitle">You argued well</div>
              </button>

              <button 
                className="submit-button-new"
                onClick={submitArgument}
                disabled={submitting || !newArgument.trim()}
              >
                {submitting ? 'SUBMITTING...' : 'SUBMIT ARGUMENT'}
              </button>
            </div>
          </div>
        </div>
      </div> {/* End input-area-container */}

      {/* Modal for Expanded Argument */}
      {expandedArgument && (
        <div className="argument-modal-overlay" onClick={() => setExpandedArgument(null)}>
          <button className="modal-close-button" onClick={() => setExpandedArgument(null)}>
            ×
          </button>
          <div className="argument-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '24px', marginBottom: '20px' }}>
              {expandedArgument.participant_role === opponentRole ? opponentName : myName}
            </h2>
            <div style={{ fontFamily: 'Abel', fontSize: '18px', lineHeight: '1.7' }}>
              {expandedArgument.content}
            </div>
            {expandedArgument.score && (
              <div style={{ marginTop: '20px', padding: '15px', background: '#f0f0f0', borderRadius: '8px' }}>
                <strong>Score: </strong>
                <span style={{ fontSize: '20px', color: expandedArgument.score.total_score > 0 ? 'green' : 'red' }}>
                  {expandedArgument.score.total_score > 0 ? '+' : ''}{expandedArgument.score.total_score}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Insights Modal */}
      {showInsightsModal && debate.insight_summary && (
        <div className="argument-modal-overlay" onClick={() => setShowInsightsModal(false)}>
          <button className="modal-close-button" onClick={() => setShowInsightsModal(false)}>
            ×
          </button>
          <div className="argument-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '32px', marginBottom: '20px', letterSpacing: '2px', textAlign: 'center' }}>
              💡 DEBATE INSIGHTS
            </h2>
            
            {debate.insight_summary.short_summary && (
              <div style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                color: 'white',
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '25px',
                fontFamily: 'Abel',
                fontSize: '16px',
                lineHeight: '1.7',
                letterSpacing: '0.5px'
              }}>
                <strong style={{ fontFamily: 'Bebas Neue', fontSize: '18px', letterSpacing: '1px' }}>Executive Summary:</strong>
                <p style={{ marginTop: '10px' }}>{debate.insight_summary.short_summary}</p>
              </div>
            )}

            <div style={{ fontFamily: 'Abel', fontSize: '15px', lineHeight: '1.7', letterSpacing: '0.5px' }}>
              {debate.insight_summary.common_ground?.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{ fontFamily: 'Bebas Neue', fontSize: '20px', letterSpacing: '1px', color: '#667eea', marginBottom: '10px' }}>
                    🤝 Common Ground
                  </h3>
                  <ul style={{ paddingLeft: '20px' }}>
                    {debate.insight_summary.common_ground.map((item, idx) => (
                      <li key={idx} style={{ marginBottom: '8px' }}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {debate.insight_summary.key_insights?.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{ fontFamily: 'Bebas Neue', fontSize: '20px', letterSpacing: '1px', color: '#667eea', marginBottom: '10px' }}>
                    💎 Key Insights
                  </h3>
                  <ul style={{ paddingLeft: '20px' }}>
                    {debate.insight_summary.key_insights.map((item, idx) => (
                      <li key={idx} style={{ marginBottom: '8px' }}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {debate.insight_summary.shifted_positions?.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{ fontFamily: 'Bebas Neue', fontSize: '20px', letterSpacing: '1px', color: '#667eea', marginBottom: '10px' }}>
                    🔄 Shifted Positions
                  </h3>
                  <ul style={{ paddingLeft: '20px' }}>
                    {debate.insight_summary.shifted_positions.map((item, idx) => (
                      <li key={idx} style={{ marginBottom: '8px' }}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {debate.insight_summary.open_questions?.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{ fontFamily: 'Bebas Neue', fontSize: '20px', letterSpacing: '1px', color: '#667eea', marginBottom: '10px' }}>
                    ❓ Open Questions
                  </h3>
                  <ul style={{ paddingLeft: '20px' }}>
                    {debate.insight_summary.open_questions.map((item, idx) => (
                      <li key={idx} style={{ marginBottom: '8px' }}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div style={{ 
                marginTop: '30px', 
                padding: '15px', 
                background: '#f0f0f0', 
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '13px',
                color: '#666'
              }}>
                Generated at: {new Date(debate.insight_summary.generated_at).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    );
  };

  // Return mobile or desktop based on screen size
  return isMobile ? renderMobile() : renderDesktop();
};

// Security Tab - 2FA & Password Management (Admin Only)
const SecurityTab = () => {
  const [twoFaStatus, setTwoFaStatus] = useState({ enabled: false, has_secret: false });
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [disabling, setDisabling] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const getToken = () => {
    return localStorage.getItem('admin_session_token') || 
           localStorage.getItem('user_session_token') || 
           localStorage.getItem('token');
  };

  const fetch2FAStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/2fa/status`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setTwoFaStatus(response.data);
    } catch (err) {
      console.error('Error fetching 2FA status:', err);
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    try {
      const response = await axios.post(`${API}/admin/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSetupData(response.data);
      toast.info('📱 Scanne den QR-Code mit deiner Authenticator App');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Setup');
    }
  };

  const verifyAndEnable = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Bitte 6-stelligen Code eingeben');
      return;
    }
    
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/2fa/verify`, { code: verifyCode }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('✅ 2FA erfolgreich aktiviert!');
      setSetupData(null);
      setVerifyCode('');
      fetch2FAStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setVerifying(false);
    }
  };

  const disable2FA = async () => {
    if (disableCode.length !== 6) {
      toast.error('Bitte 6-stelligen Code eingeben');
      return;
    }
    
    setDisabling(true);
    try {
      await axios.post(`${API}/admin/2fa/disable`, { code: disableCode }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('2FA deaktiviert');
      setDisableCode('');
      fetch2FAStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Deaktivieren');
    } finally {
      setDisabling(false);
    }
  };

  const changePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    if (twoFaStatus.enabled && totpCode.length !== 6) {
      toast.error('Bitte 6-stelligen 2FA-Code eingeben');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        totp_code: totpCode
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('✅ Passwort erfolgreich geändert!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTotpCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Ändern');
    } finally {
      setChangingPassword(false);
    }
  };

  useEffect(() => {
    fetch2FAStatus();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Lade Sicherheitseinstellungen...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* 2FA Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">🔐</span>
            Zwei-Faktor-Authentifizierung (2FA)
            {twoFaStatus.enabled && (
              <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                ✅ Aktiviert
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Schütze deinen Admin-Account mit einem zusätzlichen Sicherheitsfaktor
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!twoFaStatus.enabled ? (
            // 2FA Setup
            <div className="space-y-4">
              {!setupData ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 mb-3">
                    ⚠️ 2FA ist nicht aktiviert. Aktiviere 2FA für zusätzliche Sicherheit.
                  </p>
                  <Button onClick={startSetup}>
                    🔐 2FA einrichten
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">📱 Schritt 1: QR-Code scannen</h4>
                    <p className="text-sm text-blue-800 mb-3">
                      Scanne diesen QR-Code mit deiner Authenticator App (Google Authenticator, Authy, etc.)
                    </p>
                    <div className="flex justify-center mb-4">
                      <img 
                        src={setupData.qr_code} 
                        alt="2FA QR Code" 
                        className="w-48 h-48 border rounded"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Oder manuell eingeben:</p>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded break-all">
                        {setupData.secret}
                      </code>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-2">✅ Schritt 2: Code verifizieren</h4>
                    <p className="text-sm text-green-800 mb-3">
                      Gib den 6-stelligen Code aus deiner Authenticator App ein:
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={verifyCode}
                        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="flex-1 p-3 border rounded-md text-center text-2xl font-mono tracking-widest"
                        maxLength={6}
                      />
                      <Button 
                        onClick={verifyAndEnable} 
                        disabled={verifying || verifyCode.length !== 6}
                      >
                        {verifying ? '⏳' : '✅'} Aktivieren
                      </Button>
                    </div>
                  </div>
                  
                  <Button variant="outline" onClick={() => setSetupData(null)} className="w-full">
                    Abbrechen
                  </Button>
                </div>
              )}
            </div>
          ) : (
            // 2FA Enabled - Option to disable
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">
                  ✅ 2FA ist aktiviert. Dein Account ist durch einen zweiten Faktor geschützt.
                </p>
              </div>
              
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-semibold text-red-900 mb-2">⚠️ 2FA deaktivieren</h4>
                <p className="text-sm text-red-800 mb-3">
                  Um 2FA zu deaktivieren, gib deinen aktuellen 2FA-Code ein:
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="flex-1 p-3 border rounded-md text-center text-2xl font-mono tracking-widest"
                    maxLength={6}
                  />
                  <Button 
                    variant="destructive"
                    onClick={disable2FA} 
                    disabled={disabling || disableCode.length !== 6}
                  >
                    {disabling ? '⏳' : '🗑️'} Deaktivieren
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Password Change Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">🔑</span>
            Passwort ändern
          </CardTitle>
          <CardDescription>
            Ändere dein Admin-Passwort{twoFaStatus.enabled && ' (erfordert 2FA-Code)'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium mb-1">Aktuelles Passwort</label>
              <input
                type={showPasswords ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full p-2 border rounded-md"
                placeholder="••••••••"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Neues Passwort (min. 8 Zeichen)</label>
              <input
                type={showPasswords ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-2 border rounded-md"
                placeholder="••••••••"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Neues Passwort bestätigen</label>
              <input
                type={showPasswords ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-2 border rounded-md"
                placeholder="••••••••"
              />
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-red-500 text-sm mt-1">Passwörter stimmen nicht überein</p>
              )}
            </div>
            
            {twoFaStatus.enabled && (
              <div>
                <label className="block text-sm font-medium mb-1">2FA-Code</label>
                <input
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full p-2 border rounded-md text-center font-mono tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showPasswords"
                checked={showPasswords}
                onChange={(e) => setShowPasswords(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="showPasswords" className="text-sm text-gray-600">
                Passwörter anzeigen
              </label>
            </div>
            
            <Button 
              onClick={changePassword}
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword || (twoFaStatus.enabled && totpCode.length !== 6)}
              className="w-full"
            >
              {changingPassword ? '⏳ Wird geändert...' : '🔑 Passwort ändern'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security Tips */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-blue-900 mb-2">💡 Sicherheitstipps</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Verwende ein starkes, einzigartiges Passwort</li>
            <li>• Aktiviere 2FA für zusätzlichen Schutz</li>
            <li>• Bewahre deine 2FA-Backup-Codes sicher auf</li>
            <li>• Teile dein Passwort niemals mit anderen</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

// API Keys Management Tab (Admin Only)
const APIKeysTab = () => {
  const [keysStatus, setKeysStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Stripe form fields
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  const [stripePriceStarter, setStripePriceStarter] = useState('');
  const [stripePricePro, setStripePricePro] = useState('');
  const [stripePriceUnlimited, setStripePriceUnlimited] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  
  // Resend form fields
  const [resendApiKey, setResendApiKey] = useState('');
  const [savingResend, setSavingResend] = useState(false);
  const [testingResend, setTestingResend] = useState(false);

  // Google OAuth form fields
  const [googleOAuthStatus, setGoogleOAuthStatus] = useState(null);
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientSecret, setGoogleClientSecret] = useState('');
  const [savingGoogle, setSavingGoogle] = useState(false);

  const fetchKeysStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('user_session_token') || 
                    localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKeysStatus(response.data);
      
      // Pre-fill price IDs if available
      if (response.data?.stripe?.price_ids) {
        const prices = response.data.stripe.price_ids;
        if (prices.starter && !prices.starter.includes('REPLACE')) setStripePriceStarter(prices.starter);
        if (prices.pro && !prices.pro.includes('REPLACE')) setStripePricePro(prices.pro);
        if (prices.unlimited && !prices.unlimited.includes('REPLACE')) setStripePriceUnlimited(prices.unlimited);
      }

      // Fetch Google OAuth status
      try {
        const googleResponse = await axios.get(`${API}/admin/google-oauth`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGoogleOAuthStatus(googleResponse.data);
      } catch (err) {
        console.log('Google OAuth status not available');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Laden der API Keys');
    } finally {
      setLoading(false);
    }
  };

  const saveStripeKeys = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('user_session_token') || 
                    localStorage.getItem('token');
      
      await axios.post(`${API}/admin/api-keys/stripe`, {
        secret_key: stripeSecretKey,
        webhook_secret: stripeWebhookSecret,
        price_starter: stripePriceStarter,
        price_pro: stripePricePro,
        price_unlimited: stripePriceUnlimited
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('✅ Stripe Keys gespeichert!');
      setStripeSecretKey('');
      setStripeWebhookSecret('');
      fetchKeysStatus();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Fehler beim Speichern';
      setError(detail);
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const testStripeConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('user_session_token') || 
                    localStorage.getItem('token');
      
      const response = await axios.post(`${API}/admin/api-keys/test-stripe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('✅ Stripe Verbindung erfolgreich!');
      } else {
        toast.error(`❌ ${response.data.error}`);
      }
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.detail || 'Test fehlgeschlagen' });
    } finally {
      setTesting(false);
    }
  };

  // Save Resend API Key
  const saveResendKey = async () => {
    if (!resendApiKey || !resendApiKey.startsWith('re_')) {
      toast.error('Resend API Key muss mit "re_" beginnen');
      return;
    }
    setSavingResend(true);
    try {
      const token = localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('user_session_token') || 
                    localStorage.getItem('token');
      
      await axios.post(`${API}/admin/budget-config`, {
        resend_api_key: resendApiKey
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Resend API Key gespeichert');
      setResendApiKey('');
      fetchKeysStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingResend(false);
    }
  };

  // Test Resend API Key
  const testResendKey = async () => {
    setTestingResend(true);
    try {
      const token = localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('user_session_token') || 
                    localStorage.getItem('token');
      
      await axios.post(`${API}/admin/budget-config/test-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Test-Email wurde gesendet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Email-Test');
    } finally {
      setTestingResend(false);
    }
  };

  // Save Google OAuth Credentials
  const saveGoogleOAuth = async () => {
    if (!googleClientId || !googleClientId.endsWith('.apps.googleusercontent.com')) {
      toast.error('Ungültige Client-ID (muss mit .apps.googleusercontent.com enden)');
      return;
    }
    if (!googleClientSecret) {
      toast.error('Client Secret ist erforderlich');
      return;
    }
    
    setSavingGoogle(true);
    try {
      const token = localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('user_session_token') || 
                    localStorage.getItem('token');
      
      await axios.post(`${API}/admin/google-oauth`, {
        client_id: googleClientId,
        client_secret: googleClientSecret
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('✅ Google OAuth Credentials gespeichert');
      setGoogleClientId('');
      setGoogleClientSecret('');
      fetchKeysStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingGoogle(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchKeysStatus, 100);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Lade API Keys Status...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stripe Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">💳</span>
            Stripe Payment Integration
            <Button onClick={fetchKeysStatus} size="sm" variant="outline" className="ml-auto">
              🔄 Aktualisieren
            </Button>
          </CardTitle>
          <CardDescription>
            Konfiguriere deine Stripe API Keys für Zahlungsabwicklung
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-lg border ${keysStatus?.stripe?.secret_key?.configured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
              <div className="font-semibold text-sm mb-1">Secret Key</div>
              <div className="text-xs text-gray-600">
                {keysStatus?.stripe?.secret_key?.configured ? (
                  <>
                    <span className="text-green-600">✅ Konfiguriert</span>
                    <div className="mt-1 font-mono text-xs">{keysStatus?.stripe?.secret_key?.masked}</div>
                    <div className="text-gray-400">Quelle: {keysStatus?.stripe?.secret_key?.source}</div>
                  </>
                ) : (
                  <span className="text-orange-600">⚠️ Nicht konfiguriert</span>
                )}
              </div>
            </div>
            
            <div className={`p-4 rounded-lg border ${keysStatus?.stripe?.webhook_secret?.configured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
              <div className="font-semibold text-sm mb-1">Webhook Secret</div>
              <div className="text-xs text-gray-600">
                {keysStatus?.stripe?.webhook_secret?.configured ? (
                  <>
                    <span className="text-green-600">✅ Konfiguriert</span>
                    <div className="mt-1 font-mono text-xs">{keysStatus?.stripe?.webhook_secret?.masked}</div>
                  </>
                ) : (
                  <span className="text-orange-600">⚠️ Nicht konfiguriert</span>
                )}
              </div>
            </div>
            
            <div className={`p-4 rounded-lg border ${keysStatus?.stripe?.publishable_key?.configured ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="font-semibold text-sm mb-1">Publishable Key</div>
              <div className="text-xs text-gray-600">
                {keysStatus?.stripe?.publishable_key?.configured ? (
                  <>
                    <span className="text-green-600">✅ Konfiguriert</span>
                    <div className="mt-1 font-mono text-xs">{keysStatus?.stripe?.publishable_key?.masked}</div>
                  </>
                ) : (
                  <span className="text-gray-500">ℹ️ Via Environment</span>
                )}
              </div>
            </div>
          </div>

          {/* Test Connection */}
          <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <Button 
              onClick={testStripeConnection} 
              disabled={testing}
              variant="outline"
            >
              {testing ? '⏳ Teste...' : '🔌 Verbindung testen'}
            </Button>
            {testResult && (
              <div className={`text-sm ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {testResult.success ? (
                  <>✅ Verbunden: {testResult.business_name || testResult.account_id} ({testResult.country})</>
                ) : (
                  <>❌ {testResult.error}</>
                )}
              </div>
            )}
          </div>

          {/* Input Form */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-semibold mb-3">🔐 Neue Keys eingeben</h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Secret Key (sk_live_... oder sk_test_...)</label>
                <div className="relative">
                  <input
                    type={showSecretKey ? "text" : "password"}
                    value={stripeSecretKey}
                    onChange={(e) => setStripeSecretKey(e.target.value)}
                    placeholder="sk_live_..."
                    className="w-full p-2 border rounded-md font-mono text-sm pr-20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm"
                  >
                    {showSecretKey ? '🙈 Verbergen' : '👁️ Zeigen'}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Webhook Secret (whsec_...)</label>
                <input
                  type="password"
                  value={stripeWebhookSecret}
                  onChange={(e) => setStripeWebhookSecret(e.target.value)}
                  placeholder="whsec_..."
                  className="w-full p-2 border rounded-md font-mono text-sm"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Price ID: Starter</label>
                  <input
                    type="text"
                    value={stripePriceStarter}
                    onChange={(e) => setStripePriceStarter(e.target.value)}
                    placeholder="price_..."
                    className="w-full p-2 border rounded-md font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Price ID: Pro</label>
                  <input
                    type="text"
                    value={stripePricePro}
                    onChange={(e) => setStripePricePro(e.target.value)}
                    placeholder="price_..."
                    className="w-full p-2 border rounded-md font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Price ID: Unlimited</label>
                  <input
                    type="text"
                    value={stripePriceUnlimited}
                    onChange={(e) => setStripePriceUnlimited(e.target.value)}
                    placeholder="price_..."
                    className="w-full p-2 border rounded-md font-mono text-sm"
                  />
                </div>
              </div>
              
              <Button 
                onClick={saveStripeKeys} 
                disabled={saving || (!stripeSecretKey && !stripeWebhookSecret && !stripePriceStarter && !stripePricePro && !stripePriceUnlimited)}
                className="w-full mt-2"
              >
                {saving ? '⏳ Speichern...' : '💾 Stripe Keys speichern'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Other API Keys Overview */}
      <Card>
        <CardHeader>
          <CardTitle>🔑 Weitere API Keys (Übersicht)</CardTitle>
          <CardDescription>
            Diese Keys werden über Environment-Variablen konfiguriert
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg border ${keysStatus?.openai?.configured ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span>🟢</span>
                <span className="font-semibold">OpenAI</span>
              </div>
              {keysStatus?.openai?.configured ? (
                <div className="text-sm text-green-600">
                  ✅ Konfiguriert
                  <div className="font-mono text-xs text-gray-500 mt-1">{keysStatus?.openai?.masked}</div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">⚠️ Nicht konfiguriert</div>
              )}
            </div>
            
            <div className={`p-4 rounded-lg border ${keysStatus?.anthropic?.configured ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span>🟣</span>
                <span className="font-semibold">Anthropic</span>
              </div>
              {keysStatus?.anthropic?.configured ? (
                <div className="text-sm text-green-600">
                  ✅ Konfiguriert
                  <div className="font-mono text-xs text-gray-500 mt-1">{keysStatus?.anthropic?.masked}</div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">⚠️ Nicht konfiguriert</div>
              )}
            </div>
            
            <div className={`p-4 rounded-lg border ${keysStatus?.google?.configured ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span>🔵</span>
                <span className="font-semibold">Google (Gemini)</span>
              </div>
              {keysStatus?.google?.configured ? (
                <div className="text-sm text-green-600">
                  ✅ Konfiguriert
                  <div className="font-mono text-xs text-gray-500 mt-1">{keysStatus?.google?.masked}</div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">⚠️ Nicht konfiguriert</div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resend Email Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">📧</span>
            Resend Email Integration
          </CardTitle>
          <CardDescription>
            Konfiguriere Resend für Email-Benachrichtigungen (Budget-Warnungen)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Status */}
          <div className={`p-4 rounded-lg border ${keysStatus?.resend_api_key_configured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
            <div className="flex items-center gap-2">
              {keysStatus?.resend_api_key_configured ? (
                <>
                  <span className="text-green-600">✅</span>
                  <span className="font-semibold">Resend API Key konfiguriert</span>
                </>
              ) : (
                <>
                  <span className="text-orange-600">⚠️</span>
                  <span className="font-semibold">Resend API Key nicht konfiguriert</span>
                </>
              )}
            </div>
            {!keysStatus?.resend_api_key_configured && (
              <p className="text-sm text-orange-700 mt-2">
                Budget-Warnungen per Email erfordern einen Resend API Key
              </p>
            )}
          </div>

          {/* Input Form */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Resend API Key
              </label>
              <input
                type="password"
                value={resendApiKey}
                onChange={(e) => setResendApiKey(e.target.value)}
                placeholder="re_..."
                className="w-full p-2 border rounded-md font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Hole deinen API Key von <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">resend.com/api-keys</a> (kostenlos bis 3.000 Emails/Monat)
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={saveResendKey} 
                disabled={savingResend || !resendApiKey}
              >
                {savingResend ? '⏳ Speichern...' : '💾 API Key speichern'}
              </Button>
              <Button 
                onClick={testResendKey} 
                variant="outline" 
                disabled={testingResend || !keysStatus?.resend_api_key_configured}
              >
                {testingResend ? '⏳ Senden...' : '📧 Test-Email senden'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Google OAuth Integration (Self-Hosting) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">🔐</span>
            Google OAuth (Self-Hosting)
          </CardTitle>
          <CardDescription>
            Konfiguriere deine eigenen Google OAuth Credentials für Self-Hosting
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Status */}
          <div className={`p-4 rounded-lg border ${googleOAuthStatus?.configured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
            <div className="flex items-center gap-2">
              {googleOAuthStatus?.configured ? (
                <>
                  <span className="text-green-600">✅</span>
                  <span className="font-semibold">Google OAuth konfiguriert</span>
                </>
              ) : (
                <>
                  <span className="text-orange-600">⚠️</span>
                  <span className="font-semibold">Google OAuth nicht konfiguriert</span>
                </>
              )}
            </div>
            {googleOAuthStatus?.configured && (
              <div className="mt-2 text-sm text-gray-600">
                <div>Client-ID: <code className="bg-gray-100 px-1 rounded">{googleOAuthStatus.client_id_masked}</code></div>
                <div>Secret: {googleOAuthStatus.has_secret ? '✅ Vorhanden' : '❌ Fehlt'}</div>
              </div>
            )}
            {!googleOAuthStatus?.configured && (
              <p className="text-sm text-orange-700 mt-2">
                Für Self-Hosting benötigst du eigene Google OAuth Credentials
              </p>
            )}
          </div>

          {/* Input Form */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Google Client ID
              </label>
              <input
                type="text"
                value={googleClientId}
                onChange={(e) => setGoogleClientId(e.target.value)}
                placeholder="xxxxx.apps.googleusercontent.com"
                className="w-full p-2 border rounded-md font-mono text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Google Client Secret
              </label>
              <input
                type="password"
                value={googleClientSecret}
                onChange={(e) => setGoogleClientSecret(e.target.value)}
                placeholder="GOCSPX-xxxxx"
                className="w-full p-2 border rounded-md font-mono"
              />
            </div>

            <p className="text-xs text-gray-500">
              Erstelle Credentials in der <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Cloud Console</a>
            </p>
            
            <Button 
              onClick={saveGoogleOAuth} 
              disabled={savingGoogle || !googleClientId || !googleClientSecret}
            >
              {savingGoogle ? '⏳ Speichern...' : '💾 OAuth Credentials speichern'}
            </Button>
          </div>

          {/* Setup Instructions */}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">📋 Einrichtungs-Checkliste</h4>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Google Cloud Project erstellen</li>
              <li>OAuth-Zustimmungsbildschirm konfigurieren</li>
              <li>OAuth 2.0 Client-ID (Webanwendung) erstellen</li>
              <li>Redirect URI hinzufügen: <code className="bg-blue-100 px-1 rounded text-xs">{window.location.origin}/login</code></li>
              <li>Client ID & Secret hier eingeben</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-amber-900 mb-2">🔒 Sicherheitshinweise</h4>
          <ul className="text-sm text-amber-800 space-y-1">
            <li>• Secret Keys werden verschlüsselt in der Datenbank gespeichert</li>
            <li>• Keys werden NIEMALS vollständig angezeigt (nur maskiert)</li>
            <li>• Stripe: Verwende für Tests <code className="bg-amber-100 px-1 rounded">sk_test_</code> Keys</li>
            <li>• Stripe: Für Produktion <code className="bg-amber-100 px-1 rounded">sk_live_</code> Keys verwenden</li>
            <li>• Resend: Keys beginnen mit <code className="bg-amber-100 px-1 rounded">re_</code></li>
            <li>• Google OAuth: Client ID endet mit <code className="bg-amber-100 px-1 rounded">.apps.googleusercontent.com</code></li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

// LLM Stats Tab Component (Admin Only)
const LLMStatsTab = () => {
  const [stats, setStats] = useState(null);
  const [budgetConfig, setBudgetConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingBudget, setSavingBudget] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [resettingStats, setResettingStats] = useState(false);
  
  // Budget form state
  const [openAIBudget, setOpenAIBudget] = useState(100);
  const [anthropicBudget, setAnthropicBudget] = useState(100);
  const [geminiBudget, setGeminiBudget] = useState(50);
  const [warningThreshold, setWarningThreshold] = useState(90);
  const [resendApiKey, setResendApiKey] = useState('');

  const getToken = () => {
    return localStorage.getItem('admin_session_token') || 
           localStorage.getItem('user_session_token') || 
           localStorage.getItem('token');
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/llm-stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchBudgetConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/budget-config`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setBudgetConfig(response.data);
      setOpenAIBudget(response.data.openai_budget || 100);
      setAnthropicBudget(response.data.anthropic_budget || 100);
      setGeminiBudget(response.data.gemini_budget || 50);
      setWarningThreshold((response.data.warning_threshold || 0.9) * 100);
    } catch (err) {
      console.error('Error fetching budget config:', err);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchStats(), fetchBudgetConfig()]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const saveBudgetConfig = async () => {
    setSavingBudget(true);
    try {
      await axios.post(`${API}/admin/budget-config`, {
        openai_budget: parseFloat(openAIBudget),
        anthropic_budget: parseFloat(anthropicBudget),
        gemini_budget: parseFloat(geminiBudget),
        warning_threshold: parseFloat(warningThreshold) / 100,
        resend_api_key: resendApiKey
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('✅ Budget-Konfiguration gespeichert');
      setResendApiKey('');
      fetchBudgetConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingBudget(false);
    }
  };

  const testWarningEmail = async () => {
    setTestingEmail(true);
    try {
      await axios.post(`${API}/admin/budget-config/test-email`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('📧 Test-Email gesendet an info@debatefy.online');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email-Versand fehlgeschlagen');
    } finally {
      setTestingEmail(false);
    }
  };

  const resetStats = async () => {
    if (!window.confirm('Statistiken wirklich zurücksetzen?')) return;
    
    setResettingStats(true);
    try {
      await axios.post(`${API}/admin/budget-config/reset-stats`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('📊 Statistiken zurückgesetzt');
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally {
      setResettingStats(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchAll, 100);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Lade Statistiken...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const ProviderStatsCard = ({ name, icon, provider, budget }) => {
    const providerStats = stats?.providers_configured?.[provider] || {};
    const usage = providerStats.cost_usd || 0;
    const usagePercent = budget > 0 ? (usage / budget) * 100 : 0;
    const isWarning = usagePercent >= (warningThreshold || 90);
    
    return (
      <div className={`p-4 rounded-lg border ${providerStats.configured ? (isWarning ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200') : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl">{icon}</span>
          <span className="font-semibold">{name}</span>
          {providerStats.configured ? (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">✓ Aktiv</span>
          ) : (
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Nicht konfiguriert</span>
          )}
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>API Calls:</span>
            <span className="font-mono">{providerStats.calls || 0}</span>
          </div>
          <div className="flex justify-between">
            <span>Geschätzte Kosten:</span>
            <span className="font-mono">${usage.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Budget:</span>
            <span className="font-mono">${budget}</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all ${isWarning ? 'bg-red-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(100, usagePercent)}%` }}
            ></div>
          </div>
          <div className="text-xs text-gray-500 text-right">{usagePercent.toFixed(1)}% verbraucht</div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Usage Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🤖 LLM API Nutzung
            <div className="ml-auto flex gap-2">
              <Button onClick={resetStats} size="sm" variant="outline" disabled={resettingStats}>
                {resettingStats ? '⏳' : '🗑️'} Reset
              </Button>
              <Button onClick={fetchAll} size="sm" variant="outline">
                🔄 Aktualisieren
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            Übersicht über API-Nutzung und Kosten (ohne Fallback - nur eigene Keys)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
              <div className="text-3xl font-bold text-blue-700">{stats?.total_calls || 0}</div>
              <div className="text-sm text-blue-600">Gesamt API Calls</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200 text-center">
              <div className="text-3xl font-bold text-green-700">${(stats?.total_estimated_cost_usd || 0).toFixed(2)}</div>
              <div className="text-sm text-green-600">Geschätzte Kosten</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 text-center">
              <div className="text-3xl font-bold text-purple-700">
                {Object.values(stats?.warnings_sent || {}).filter(Boolean).length}
              </div>
              <div className="text-sm text-purple-600">Warnungen gesendet</div>
            </div>
          </div>

          {/* Provider Stats */}
          <h4 className="font-semibold mb-3">Provider-Status</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ProviderStatsCard name="OpenAI" icon="🟢" provider="openai" budget={openAIBudget} />
            <ProviderStatsCard name="Anthropic" icon="🟣" provider="anthropic" budget={anthropicBudget} />
            <ProviderStatsCard name="Google Gemini" icon="🔵" provider="gemini" budget={geminiBudget} />
          </div>
        </CardContent>
      </Card>

      {/* Budget Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>💰 Budget & Warnungen</CardTitle>
          <CardDescription>
            Konfiguriere Budget-Limits und Email-Warnungen bei niedrigen Credits
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">OpenAI Budget (USD)</label>
              <input
                type="number"
                value={openAIBudget}
                onChange={(e) => setOpenAIBudget(e.target.value)}
                className="w-full p-2 border rounded-md"
                min="0"
                step="10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Anthropic Budget (USD)</label>
              <input
                type="number"
                value={anthropicBudget}
                onChange={(e) => setAnthropicBudget(e.target.value)}
                className="w-full p-2 border rounded-md"
                min="0"
                step="10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Gemini Budget (USD)</label>
              <input
                type="number"
                value={geminiBudget}
                onChange={(e) => setGeminiBudget(e.target.value)}
                className="w-full p-2 border rounded-md"
                min="0"
                step="10"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Warnung bei Verbrauch von (%)
              </label>
              <input
                type="number"
                value={warningThreshold}
                onChange={(e) => setWarningThreshold(e.target.value)}
                className="w-full p-2 border rounded-md"
                min="50"
                max="99"
              />
              <p className="text-xs text-gray-500 mt-1">
                Email wird bei {warningThreshold}% Verbrauch gesendet (= {100 - warningThreshold}% verbleibend)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Resend API Key (für Email-Versand)
              </label>
              <input
                type="password"
                value={resendApiKey}
                onChange={(e) => setResendApiKey(e.target.value)}
                placeholder={budgetConfig?.resend_api_key_configured ? '••••• (bereits konfiguriert)' : 're_...'}
                className="w-full p-2 border rounded-md"
              />
              {budgetConfig?.resend_api_key_configured && (
                <p className="text-xs text-green-600 mt-1">✅ Email-Versand ist konfiguriert</p>
              )}
            </div>
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button onClick={saveBudgetConfig} disabled={savingBudget}>
              {savingBudget ? '⏳ Speichern...' : '💾 Budget speichern'}
            </Button>
            <Button 
              onClick={testWarningEmail} 
              variant="outline" 
              disabled={testingEmail || !budgetConfig?.resend_api_key_configured}
            >
              {testingEmail ? '⏳ Senden...' : '📧 Test-Email senden'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-amber-900 mb-2">⚠️ Wichtig für Self-Hosting</h4>
          <ul className="text-sm text-amber-800 space-y-1">
            <li>• <strong>Kein Fallback:</strong> Nur deine eigenen API Keys werden verwendet</li>
            <li>• <strong>Email-Warnung:</strong> Du erhältst eine Email an info@debatefy.online wenn das Budget knapp wird</li>
            <li>• <strong>Resend API Key:</strong> Erforderlich für Email-Versand (kostenlos bei resend.com)</li>
            <li>• Kosten sind <strong>Schätzungen</strong> basierend auf durchschnittlichen Token-Zahlen</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Panel
const AdminPanel = () => {
  const navigate = useNavigate();
  const [theses, setTheses] = useState([]);
  const [newThesis, setNewThesis] = useState('');
  const [bulkTheses, setBulkTheses] = useState('');
  const [thesisLanguage, setThesisLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('theses');
  
  // Feedback state
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  
  // Archive management state
  const [allDebates, setAllDebates] = useState({ active: [], archived: [] });
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveFilter, setArchiveFilter] = useState('all'); // 'all', 'active', 'archived'

  useEffect(() => {
    fetchTheses();
    fetchFeedbacks();
    fetchAllDebates();
  }, []);
  
  useEffect(() => {
    fetchFeedbacks();
  }, [feedbackFilter]);
  
  // Fetch all debates for admin
  const fetchAllDebates = async () => {
    setArchiveLoading(true);
    try {
      const token = localStorage.getItem('admin_session_token');
      const response = await axios.get(`${API}/admin/all-debates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllDebates(response.data);
    } catch (error) {
      console.error('Error fetching debates:', error);
    } finally {
      setArchiveLoading(false);
    }
  };
  
  // Selected debates for bulk actions
  const [selectedDebates, setSelectedDebates] = useState([]);
  
  // Toggle single debate selection
  const toggleDebateSelection = (debateId, source) => {
    const key = `${source}:${debateId}`;
    setSelectedDebates(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };
  
  // Select all visible debates
  const selectAllDebates = () => {
    const allKeys = [];
    if (archiveFilter === 'all' || archiveFilter === 'active') {
      allDebates.active?.forEach(d => allKeys.push(`active:${d.id}`));
    }
    if (archiveFilter === 'all' || archiveFilter === 'archived') {
      allDebates.archived?.forEach(d => allKeys.push(`archive:${d.id}`));
    }
    setSelectedDebates(allKeys);
  };
  
  // Deselect all
  const deselectAllDebates = () => {
    setSelectedDebates([]);
  };
  
  // Delete a debate
  const deleteDebate = async (debateId, source) => {
    if (!window.confirm('Are you sure you want to delete this debate? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_session_token');
      const endpoint = source === 'archive' 
        ? `${API}/archive/${debateId}`
        : `${API}/debate/${debateId}`;
      
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Debate deleted successfully');
      fetchAllDebates();
    } catch (error) {
      console.error('Error deleting debate:', error);
      toast.error(error.response?.data?.detail || 'Error deleting debate');
    }
  };
  
  // Bulk delete selected debates
  const deleteSelectedDebates = async () => {
    if (selectedDebates.length === 0) {
      toast.error('No debates selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedDebates.length} debate(s)? This cannot be undone.`)) {
      return;
    }
    
    const token = localStorage.getItem('admin_session_token');
    let successCount = 0;
    let errorCount = 0;
    
    for (const key of selectedDebates) {
      const [source, debateId] = key.split(':');
      const endpoint = source === 'archive' 
        ? `${API}/archive/${debateId}`
        : `${API}/debate/${debateId}`;
      
      try {
        await axios.delete(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        successCount++;
      } catch (error) {
        console.error(`Error deleting debate ${debateId}:`, error);
        errorCount++;
      }
    }
    
    setSelectedDebates([]);
    fetchAllDebates();
    
    if (errorCount === 0) {
      toast.success(`${successCount} debate(s) deleted successfully`);
    } else {
      toast.warning(`${successCount} deleted, ${errorCount} failed`);
    }
  };

  const fetchTheses = async () => {
    try {
      const response = await axios.get(`${API}/theses`);
      setTheses(response.data);
    } catch (error) {
      console.error('Error fetching theses:', error);
    }
  };

  const addThesis = async () => {
    if (!newThesis.trim()) {
      toast.error('Please enter a thesis');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/theses`, { 
        text: newThesis,
        language: thesisLanguage 
      });
      setNewThesis('');
      toast.success('Thesis added!');
      fetchTheses();
    } catch (error) {
      console.error('Error adding thesis:', error);
      toast.error('Error adding thesis');
    } finally {
      setLoading(false);
    }
  };

  const addBulkTheses = async () => {
    if (!bulkTheses.trim()) {
      toast.error('Please enter theses (one per line)');
      return;
    }

    const thesesArray = bulkTheses.split('\n').filter(t => t.trim());
    setLoading(true);
    try {
      await axios.post(`${API}/theses/bulk`, { 
        theses: thesesArray,
        language: thesisLanguage 
      });
      setBulkTheses('');
      toast.success(`${thesesArray.length} theses added!`);
      fetchTheses();
    } catch (error) {
      console.error('Error adding bulk theses:', error);
      toast.error('Error adding theses');
    } finally {
      setLoading(false);
    }
  };

  const deleteThesis = async (id) => {
    try {
      await axios.delete(`${API}/theses/${id}`);
      toast.success('Thesis deleted!');
      fetchTheses();
    } catch (error) {
      console.error('Error deleting thesis:', error);
      toast.error('Error deleting thesis');
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error('Please select a logo file');
      return;
    }

    const formData = new FormData();
    formData.append('logo', logoFile);

    setLogoUploading(true);
    try {
      await axios.post(`${API}/branding/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Logo uploaded successfully!');
      setLogoFile(null);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Error uploading logo');
    } finally {
      setLogoUploading(false);
    }
  };
  
  const fetchFeedbacks = async () => {
    setFeedbackLoading(true);
    try {
      const filterQuery = feedbackFilter !== 'all' ? `?status=${feedbackFilter}` : '';
      const response = await axios.get(`${API}/feedback/list${filterQuery}`);
      setFeedbacks(response.data.feedbacks || []);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };
  
  const updateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      await axios.patch(`${API}/feedback/${feedbackId}/status`, null, {
        params: { status: newStatus }
      });
      fetchFeedbacks();
      toast.success('Status updated!');
    } catch (error) {
      console.error('Error updating feedback status:', error);
      toast.error('Error updating status');
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('admin_session_token');
    localStorage.removeItem('admin_user');
    toast.info('Erfolgreich abgemeldet');
    navigate('/admin/login');
  };

  return (
    <div className="page-container">
      <Header />
      <Card>
        <CardHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
            <div>
              <CardTitle>Admin Panel</CardTitle>
              <CardDescription>Manage content, theses, and analytics</CardDescription>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/vouchers')}
              >
                🎟️ Vouchers
              </Button>
              <span className="text-gray-300">|</span>
              <Button
                variant={activeTab === 'llmstats' ? 'default' : 'ghost'}
                size="sm"
                className={activeTab === 'llmstats' ? '' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'}
                onClick={() => setActiveTab('llmstats')}
              >
                🤖 LLM Stats
              </Button>
              <Button
                variant={activeTab === 'apikeys' ? 'default' : 'ghost'}
                size="sm"
                className={activeTab === 'apikeys' ? '' : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50'}
                onClick={() => setActiveTab('apikeys')}
              >
                🔑 API Keys
              </Button>
              <Button
                variant={activeTab === 'security' ? 'default' : 'ghost'}
                size="sm"
                className={activeTab === 'security' ? '' : 'text-amber-600 hover:text-amber-800 hover:bg-amber-50'}
                onClick={() => setActiveTab('security')}
              >
                🔐 Sicherheit
              </Button>
              <span className="text-gray-300">|</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
              >
                🚪 Abmelden
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5 md:grid-cols-10 gap-1 h-auto mb-4">
              <TabsTrigger value="theses" className="text-xs md:text-sm py-2">Theses</TabsTrigger>
              <TabsTrigger value="archive" className="text-xs md:text-sm py-2">🗂️ Archive</TabsTrigger>
              <TabsTrigger value="logo" className="text-xs md:text-sm py-2">Logo</TabsTrigger>
              <TabsTrigger value="content" className="text-xs md:text-sm py-2">Content</TabsTrigger>
              <TabsTrigger value="analytics" className="text-xs md:text-sm py-2">Analytics</TabsTrigger>
              <TabsTrigger value="feedback" className="text-xs md:text-sm py-2">📝 Feedback</TabsTrigger>
              <TabsTrigger value="design" className="text-xs md:text-sm py-2">🎨 Design</TabsTrigger>
              <TabsTrigger value="funfacts" className="text-xs md:text-sm py-2">💡 Facts</TabsTrigger>
              <TabsTrigger value="legal" className="text-xs md:text-sm py-2">📄 Legal</TabsTrigger>
              <TabsTrigger value="about" className="text-xs md:text-sm py-2">ℹ️ About</TabsTrigger>  
          </TabsList>
            
            <TabsContent value="theses" className="space-y-4">
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={thesisLanguage} onValueChange={setThesisLanguage}>
                  <SelectTrigger data-testid="thesis-language-bulk-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="it">Italiano</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bulk-theses">Add Theses (one per line)</Label>
                <Textarea
                  id="bulk-theses"
                  data-testid="bulk-theses-input"
                  placeholder="Thesis 1\nThesis 2\nThesis 3..."
                  value={bulkTheses}
                  onChange={(e) => setBulkTheses(e.target.value)}
                  rows={6}
                />
              </div>
              <Button 
                data-testid="add-bulk-theses-btn"
                onClick={addBulkTheses} 
                disabled={loading}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Theses
              </Button>
              
              <Separator className="my-4" />
              
              <div className="space-y-2">
                <h3 className="font-semibold">Existing Theses</h3>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {theses.map((thesis) => (
                      <Card key={thesis.id}>
                        <CardContent className="flex items-center justify-between p-4 gap-4">
                          <div className="flex-1">
                            <p>{thesis.text}</p>
                            <Badge variant="outline" className="mt-2">
                              {thesis.language?.toUpperCase() || 'EN'}
                            </Badge>
                          </div>
                          <Button
                            data-testid={`delete-thesis-${thesis.id}-btn`}
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteThesis(thesis.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </TabsContent>
            
            <TabsContent value="archive" className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <h3 className="font-semibold">Manage Debates</h3>
                  <Select value={archiveFilter} onValueChange={(v) => { setArchiveFilter(v); setSelectedDebates([]); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Debates</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="archived">Archived Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={selectAllDebates}>
                    ☑️ Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAllDebates} disabled={selectedDebates.length === 0}>
                    ☐ Deselect
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={deleteSelectedDebates}
                    disabled={selectedDebates.length === 0}
                  >
                    🗑️ Delete ({selectedDebates.length})
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchAllDebates} disabled={archiveLoading}>
                    🔄
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{allDebates.total_active || 0}</p>
                    <p className="text-sm text-gray-500">Active Debates</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{allDebates.total_archived || 0}</p>
                    <p className="text-sm text-gray-500">Archived Debates</p>
                  </CardContent>
                </Card>
              </div>
              
              {archiveLoading ? (
                <p className="text-center py-8">Loading debates...</p>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {/* Active Debates */}
                    {(archiveFilter === 'all' || archiveFilter === 'active') && allDebates.active?.map((debate) => (
                      <Card 
                        key={debate.id} 
                        className={`border-l-4 border-l-blue-500 ${selectedDebates.includes(`active:${debate.id}`) ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedDebates.includes(`active:${debate.id}`)}
                              onChange={() => toggleDebateSelection(debate.id, 'active')}
                              className="mt-1 h-4 w-4 cursor-pointer"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="bg-blue-50">Active</Badge>
                                <Badge variant="outline">{debate.status}</Badge>
                                <Badge variant="outline">{debate.language?.toUpperCase()}</Badge>
                              </div>
                              <p className="font-medium text-sm mb-1">{debate.thesis?.substring(0, 100)}{debate.thesis?.length > 100 ? '...' : ''}</p>
                              <p className="text-xs text-gray-500">
                                Created: {new Date(debate.created_at).toLocaleString('de-DE')}
                              </p>
                              <p className="text-xs text-gray-500">
                                Score: A={debate.score_a || 0} | B={debate.score_b || 0}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => deleteDebate(debate.id, 'active')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {/* Archived Debates */}
                    {(archiveFilter === 'all' || archiveFilter === 'archived') && allDebates.archived?.map((debate) => (
                      <Card 
                        key={debate.id} 
                        className={`border-l-4 border-l-green-500 ${selectedDebates.includes(`archive:${debate.id}`) ? 'ring-2 ring-green-400 bg-green-50' : ''}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedDebates.includes(`archive:${debate.id}`)}
                              onChange={() => toggleDebateSelection(debate.id, 'archive')}
                              className="mt-1 h-4 w-4 cursor-pointer"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="bg-green-50">Archived</Badge>
                                <Badge variant="outline">{debate.language?.toUpperCase()}</Badge>
                                {debate.winner && <Badge variant="outline">Winner: {debate.winner.toUpperCase()}</Badge>}
                              </div>
                              <p className="font-medium text-sm mb-1">{debate.thesis?.substring(0, 100)}{debate.thesis?.length > 100 ? '...' : ''}</p>
                              <p className="text-xs text-gray-500">
                                Created: {new Date(debate.created_at).toLocaleString('de-DE')}
                              </p>
                              <p className="text-xs text-gray-500">
                                Final Score: A={debate.score_a || 0} | B={debate.score_b || 0}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => deleteDebate(debate.id, 'archive')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {(!allDebates.active?.length && !allDebates.archived?.length) && (
                      <p className="text-center py-8 text-gray-500">No debates found</p>
                    )}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>
            
            <TabsContent value="logo" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="logo-upload">Upload Logo</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files[0])}
                    data-testid="logo-upload-input"
                  />
                  <Button 
                    onClick={handleLogoUpload}
                    disabled={logoUploading || !logoFile}
                    data-testid="upload-logo-btn"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {logoUploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Supported formats: PNG, JPG, SVG. Max size: 2MB
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="content" className="space-y-4">
              <ContentManagementTab />
            </TabsContent>
            
            <TabsContent value="analytics" className="space-y-4">
              <AnalyticsTab />
            </TabsContent>
	    
 	    <TabsContent value="about" className="space-y-4">
              <Card>
                <CardContent className="p-6">
                  <p className="mb-4">Der About-Editor ist auf einer separaten Seite verfügbar:</p>
                  <Button onClick={() => window.open('/admin/about', '_blank')}>
                    📝 About-Seite bearbeiten
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="feedback" className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-2">
                  <Button
                    variant={feedbackFilter === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFeedbackFilter('all')}
                  >
                    Alle
                  </Button>
                  <Button
                    variant={feedbackFilter === 'pending' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFeedbackFilter('pending')}
                  >
                    Ausstehend
                  </Button>
                  <Button
                    variant={feedbackFilter === 'reviewed' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFeedbackFilter('reviewed')}
                  >
                    Überprüft
                  </Button>
                  <Button
                    variant={feedbackFilter === 'implemented' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFeedbackFilter('implemented')}
                  >
                    Implementiert
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('/admin/feedback', '_blank')}
                >
                  🔗 In Vollansicht öffnen
                </Button>
              </div>
              
              <ScrollArea className="h-[600px]">
                {feedbackLoading ? (
                  <div className="text-center py-8 text-gray-500">Lade Feedbacks...</div>
                ) : feedbacks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">Keine Feedbacks gefunden.</div>
                ) : (
                  <div className="space-y-4">
                    {feedbacks.map((feedback) => (
                      <Card key={feedback.id}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant={
                                feedback.status === 'pending' ? 'secondary' :
                                feedback.status === 'reviewed' ? 'default' :
                                'outline'
                              }>
                                {feedback.status}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {new Date(feedback.created_at).toLocaleDateString('de-DE')}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setExpandedFeedback(
                                expandedFeedback === feedback.id ? null : feedback.id
                              )}
                            >
                              {expandedFeedback === feedback.id ? '▼' : '▶'}
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Beschwerde:</p>
                            <p className="text-sm">{feedback.complaint}</p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Vorschlag:</p>
                            <p className="text-sm">{feedback.suggestion}</p>
                          </div>
                          
                          {expandedFeedback === feedback.id && (
                            <>
                              <Separator />
                              <div>
                                <p className="text-sm font-semibold text-gray-700">Argument:</p>
                                <p className="text-sm bg-gray-50 p-2 rounded">{feedback.argument_text}</p>
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-700">AI-Bewertung:</p>
                                <p className="text-sm bg-gray-50 p-2 rounded">
                                  Punkte: {feedback.ai_rating?.score?.total_score || 0} | 
                                  Valid: {feedback.ai_rating?.is_valid ? 'Ja' : 'Nein'}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-700">Thesis:</p>
                                <p className="text-sm">{feedback.debate_context?.thesis}</p>
                              </div>
                              <Separator />
                              <div className="flex gap-2">
                                {feedback.status !== 'pending' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updateFeedbackStatus(feedback.id, 'pending')}
                                  >
                                    → Ausstehend
                                  </Button>
                                )}
                                {feedback.status !== 'reviewed' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updateFeedbackStatus(feedback.id, 'reviewed')}
                                  >
                                    → Überprüft
                                  </Button>
                                )}
                                {feedback.status !== 'implemented' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updateFeedbackStatus(feedback.id, 'implemented')}
                                  >
                                    → Implementiert
                                  </Button>
                                )}
                              </div>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="design" className="space-y-4">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>🎨 Design Manager</CardTitle>
                    <CardDescription>
                      Verwalte alle Grafiken für die Plattform
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => navigate('/admin/design')}
                      size="lg"
                      className="w-full"
                    >
                      🎨 Design Manager öffnen
                    </Button>
                    <p className="text-sm text-gray-600 mt-4">
                      Im Design Manager kannst du alle Grafiken für die Landingpage und das Debattentool hochladen und verwalten.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="funfacts" className="space-y-4">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>💡 Generic Fun Facts Manager</CardTitle>
                    <CardDescription>
                      Verwalte generische Fun Facts pro Sprache (werden sofort angezeigt)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => navigate('/admin/fun-facts')}
                      size="lg"
                      className="w-full"
                    >
                      💡 Fun Facts Manager öffnen
                    </Button>
                    <p className="text-sm text-gray-600 mt-4">
                      Hinterlege generische Fun Facts pro Sprache. Diese werden sofort beim ersten Argument angezeigt, während thesis-spezifische Facts im Hintergrund geladen werden.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="legal" className="space-y-4">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>📄 Rechtliche Dokumente</CardTitle>
                    <CardDescription>
                      Datenschutzerklärung & Nutzungsbedingungen in allen Sprachen bearbeiten
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => navigate('/admin/legal')}
                      size="lg"
                      className="w-full"
                    >
                      📄 Rechtliches bearbeiten
                    </Button>
                    <p className="text-sm text-gray-600 mt-4">
                      Bearbeite Datenschutzerklärung und Nutzungsbedingungen in 5 Sprachen (de, en, es, fr, it). DSGVO-konform!
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="llmstats" className="space-y-4">
              <LLMStatsTab />
            </TabsContent>
            
            <TabsContent value="apikeys" className="space-y-4">
              <APIKeysTab />
            </TabsContent>
            
            <TabsContent value="security" className="space-y-4">
              <SecurityTab />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <DesignProvider>
            <Routes>
            <Route path="/" element={<LandingPageWrapper />} />
            <Route path="/choose-mode" element={<ChooseMode />} />
            <Route path="/login" element={<UserLogin />} />
            <Route path="/register" element={<UserRegister />} />
            <Route path="/pricing" element={<PricingPage />} />
            {/* Test route removed - now using /debate/:id */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/setup" element={<SessionSetup />} />
            <Route path="/configure" element={<DebateConfig />} />
            <Route path="/invite/:inviteCode" element={<InvitePage />} />
            <Route path="/join/:inviteCode" element={<JoinPage />} />
            <Route path="/debate/:id" element={<DebateRoomRouter />} />
            <Route path="/archive" element={<ArchivePage />} />
            <Route path="/archive/view/:debateId" element={<ArchivedDebateView />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <ProtectedAdminRoute>
                <AdminPanel />
              </ProtectedAdminRoute>
            } />
            <Route path="/admin/feedback" element={
              <ProtectedAdminRoute>
                <FeedbackAdmin />
              </ProtectedAdminRoute>
            } />
            <Route path="/admin/design" element={
              <ProtectedAdminRoute>
                <DesignManager />
              </ProtectedAdminRoute>
            } />
            <Route path="/admin/fun-facts" element={
              <ProtectedAdminRoute>
                <GenericFunFactsManager />
              </ProtectedAdminRoute>
            } />
            <Route path="/admin/legal" element={
              <ProtectedAdminRoute>
                <LegalEditor />
              </ProtectedAdminRoute>
            } />
            <Route path="/admin/about" element={
              <ProtectedAdminRoute>
                <AboutEditor />
              </ProtectedAdminRoute>
            } />
            <Route path="/admin/vouchers" element={
              <ProtectedAdminRoute>
                <VoucherManager />
              </ProtectedAdminRoute>
            } />
            
            {/* Feature Detail Pages */}
            <Route 
              path="/factchecking" 
              element={<FeatureDetailPage featureKey="factchecking" defaultTitle="Fact-Checking" />} 
            />
            <Route 
              path="/insight-scoring" 
              element={<FeatureDetailPage featureKey="insight-scoring" defaultTitle="Insight Scoring" />} 
            />
            <Route 
              path="/collaborative-growth" 
              element={<FeatureDetailPage featureKey="collaborative-growth" defaultTitle="Collaborative Growth" />} 
            />
            
            {/* About Page */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/imprint" element={<Imprint />} />
            </Routes>
          </DesignProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
